// imperial-planet/next — the 4thgen worker/baked renderer wrapped in the
// Planet class. Extracted from the standalone bench page
// (labs/planet-4rdgen-2/viz/planet.js, "v58") into the shared Planet contract
// (../common/planet-interface.js).
//
// DESIGN LINE (see the contract): the lib provides the planet + terrain ONLY.
//   - The lib OWNS: generation (the init worker), the GPU bakes (rtA / material
//     atlases), the planet mesh/Object3D, materials, uniforms, tile math.
//   - The HOST owns: the WebGLRenderer, scene, camera, the animation loop, the
//     light's transform, ALL input (mouse/touch/keyboard/XR), and world nav.
// The host passes its `renderer` in (the bakes need a GL context), adds
// `planet.object3d` to its scene, and feeds per-frame state via update().
//
// WHAT WAS STRIPPED from the bench page (all HOST concerns, now gone):
//   - the ~50 `new URLSearchParams(location.search)` knobs → the options object
//     documented below (with the bench's DESKTOP defaults);
//   - scene / camera / WebGLRenderer / OrbitControls / setAnimationLoop;
//   - canvas + VRButton append to document.body, the HUD div, the progress div;
//   - keydown / resize listeners, globalThis.DBG;
//   - ALL XR locomotion/controller code (VRButton, updateXRControls,
//     xrControllers, sun-follows-camera, the sessionstart/end auto-build). The
//     DEFER concept survives as: the host decides WHEN to call build().
//   - per-zone zoom refinement (already dead in the bench — uZoomChart stays -1;
//     the zoom render-targets are not allocated here).
//
// M2 PHASE 1 = embeddable extraction ONLY. Terraform re-bake, faceIndex picking,
// and the atmosphere halo are left as clearly-marked TODO stubs (PHASE 2).

import * as THREE from "three";
import { EROSION_GLSL } from "./mountain-stamps.js";
import { PLANET_TYPES, gasPalette } from "../terrain/planet-terrain.js"; // atmosphere halo colour by type; gas-giant band palette

// ── options: the bench's ~50 URL knobs, promoted to a plain object ────────────
// Every value defaults to the bench's DESKTOP setting (the bench derived some
// defaults from a UA sniff for standalone headsets — that is a HOST decision, so
// the lib does not sniff; a host targeting weak hardware passes quality:'low').
const DEFAULTS = {
	// ── injection (host-owned resources) ──
	renderer: null,          // REQUIRED for build(): the host's THREE.WebGLRenderer
	                         // (the GPU bakes need its GL context). The host owns it.
	workerThreeUrl: "https://esm.sh/three@0.183.2", // three URL the init WORKER
	                         // imports for its internal Vector3 math (data crosses
	                         // as typed arrays). Point it at the game's own module
	                         // to drop the CDN dependency.
	workerUrl: null,         // absolute URL to a SERVED copy of planet-init-worker.js.
	                         // Required under a bundler (esbuild/webpack) that can't
	                         // serve the worker at import.meta.url. Standalone omits it.
	atlasUrl: "../assets/terrain.jpg", // 4×4 terrain atlas (terrain.jpg). ONLY fetched
	                         // for LAVA planets (real lava samples cell 10 from it).
	                         // Default resolves relative to the standalone example
	                         // page; the game passes its served path, e.g.
	                         // "/assets/gfx/planet/shader-assets/terrain.jpg".
	// ── pipeline preset ──
	quality: null,           // 'low' → baked pipeline (weak-hardware preset =
	                         // the bench's standaloneXR bundle, see XR_PRESET);
	                         // 'high' → full live shading; null → live (desktop).
	preset: null,            // 'xr' → same weak-hardware bundle as quality:'low'.
	baked: null,             // explicit override of the quality→baked mapping.
	simple: false,           // ?simple=1 : bare renderer (no bake, no breakup).
	// ── bake runner (per-frame chunking) ──
	chunksPerFrame: null,    // bake tasks drained per update(). null → the bench's
	                         // `standaloneXR ? 1 : 4`: 1 under the low/XR preset
	                         // (one chart-sized GPU job per frame — the Pico
	                         // compositor can't take more), 4 otherwise.
	onReady: null,           // called with `this` when the bake queue drains.
	bakeSlice: null,         // null = off (the bakes run exactly as above). An object
	                         // { tileSize: 64, maxTilesPerFrame: 2, flush: true } (or
	                         // `true` for those values) slices EVERY bake pass (atlas
	                         // A + both material layers per chart) into tileSize² draws
	                         // (scissor rects over the unsliced draws: identical texels),
	                         // runs at most maxTilesPerFrame of them per update() (it
	                         // replaces chunksPerFrame for this planet) and gl.flush()es
	                         // after each, so the driver submits every slice on its own:
	                         // no submission outlives a weak iGPU's preemption timeout
	                         // (Adelheid, Intel HD 400, i915 "preemption time out").
	// ── resolutions ──
	ares: 1024,              // material-atlas layer resolution = per-planet LOD.
	zres: 2048,              // (compiled into the shader; zoom refinement is off).
	btile: 0,                // >0 tiles the load bakes (0 = fast full-layer bakes).
	bres: 768,               // height/normal atlas (rtA) resolution.
	mres: 192,               // CPU mask/biome raster resolution.
	sres: 512,               // sphere geometry tessellation (SphereGeometry segs).
	// ── water / waves ──
	wave: 0.1,               // animated water ripple amplitude (0 = still).
	wsmooth: 0.8,            // water specular smoothness (0.95=mirror→glare).
	water: true,             // false = drained (show the ground itself).
	// ── charts / detail ──
	maxCharts: 24,           // k-means chart cap (clamped 1..30).
	pdet: 2,                 // extra live-erosion octaves beyond the baked count.
	liveErosion: null,       // default: !baked. Per-pixel erosion continuation.
	breakup: true,           // live micro-detail (rock/grass breakup octaves).
	breakupOctaves: 12,      // breakup octave count (1..12).
	matBakeOctaves: 12,      // breakup octaves folded into the material bake.
	detailTexture: false,    // bake breakup into a texture instead of live fbm.
	dres: 128,               // detail-texture resolution (detailTexture only).
	// ── the plane recipe (PLANE units — plane.js verbatim) ──
	free: 0.006,             // land freeboard above sea (beach readability).
	basin: 0.13,             // ocean-basin depth below sea.
	peakh: 0.065,            // mountain peak height.
	bumpr: 1.35,             // bump radius multiplier.
	bumps: 0.005,            // tiny fbm bump amplitude.
	shelfSea: 0.02,          // continental-shelf depth below sea.
	smk: 60,                 // smooth-max tightness for the bump field.
	efade: 0.06,             // height above plain where erosion reaches full strength.
	efloor: 0.006,           // keep plains out of the erosion window.
	eoff: -0.5,              // EROSION_HEIGHT_OFFSET (-1 only lowers).
	erosion: {               // runevision erosion-filter params.
		scale: 0.08333, strength: 0.16, power: 0.6,
		cell: 1.0, octaves: 5, gain: 0.5, lacunarity: 2.0,
	},
	// ── mountains ──
	mbase: 0.75,             // peak-height base (worker phOf).
	mbonus: 0.32,            // peak-height height bonus.
	// ── misc ──
	gridColor: 0xe0b84d,     // hex-grid line colour. Under a LINEAR-output renderer
	                         // (no gamma), pass a brighter value — standard line
	                         // materials aren't gamma-corrected like the surface is.
	gridOpacity: 0.5,        // hex-grid line opacity.
	exposure: 1.0,           // final-image exposure multiplier before tonemap.
	                         // <1 dims (e.g. a linear-output host that reads bright).
	atmosphere: true,        // thin Fresnel rim halo (BackSide additive shell,
	                         // rim-lit by uSunDir). false disables it entirely.
	floatTextures: false,    // ?rtf=32 : full-float rtA (else half-float).
	erosionOn: true,         // initial erosion toggle state ('e' in the bench).
	dbgChart: false,         // tint fragments by chart to see boundaries.
	// NOTE: the lib does NOT own a sun. Lighting direction is 100% host-driven:
	// the host MUST call update({ sunDir }) every frame with the planet→light
	// direction (world space). uSunDir merely starts at a neutral placeholder so
	// the first pre-update frame isn't NaN. See examples/host-next.js.
};

// ── the weak-hardware / XR preset (quality:'low' OR preset:'xr') ──────────────
// FAITHFUL restoration of the bench's `standaloneXR` bundle. Every value here is
// exactly what the bench derives when it detects a standalone headset; a host
// targeting a Pico/Quest passes quality:'low' (or preset:'xr') to get ALL of
// them together. Individual opts still override any single knob.
//
//   knob            low/XR value   desktop      bench ref (viz/planet.js)
//   ────────────────────────────────────────────────────────────────────────
//   baked           true           false        L42-44  standaloneXR → BAKED
//   bres            256            768          L66     standaloneXR?(BAKED?256:64):768
//   mres            64             192          L67     standaloneXR?64:192
//   sres            256            512          L68     standaloneXR?(BAKED?256:64):512
//   wave            0              0.1          L71     standaloneXR?0:0.1
//   pdet            2              2            L77     standaloneXR&&!BAKED?-8:2 (BAKED⇒2)
//   liveErosion     false          true         L78     !BAKED gate ⇒ false
//   breakupOctaves  8              12           L80     standaloneXR?8:12
//   chunksPerFrame  1              4            L1204   CHUNKS_PER_FRAME standaloneXR?1:4
//
// NOT lib knobs (they live in the HOST, see examples/host-next.js):
//   SUN_MODE='fixed' (L92) and renderer.xr.setFoveation (host's renderer).
// NOT changed (bench keeps desktop value): matBakeOctaves stays 12 — the
//   material bake is one-time, so it affords full octaves (bench L82:
//   `q.has("bkoct") ? BREAKUP_OCTAVES : 12`).
const XR_PRESET = {
	baked: true,
	bres: 256,
	mres: 64,
	sres: 256,
	// ares (baked color+normal atlas layer res) MUST scale down with the tier:
	// the two WebGLArrayRenderTargets cost 2 × NCC(≤24) × ares² × 4B — at the
	// 1024 default that is up to ~192 MB GPU PER PLANET, the whole-sector OOM.
	// 256 → ~12 MB; at low-tier viewing distance the difference is invisible.
	// 2026-09-28: DO NOT lift this to 1024 for the headset — a sector of 1024
	// planets is exactly what crashed the Pico in the game (user, 2026-09-28).
	ares: 256,
	wave: 0,
	pdet: 2,
	liveErosion: false,
	breakupOctaves: 8,
	chunksPerFrame: 1,
};

// ── the MEDIUM preset (quality:'medium') ─────────────────────────────────────
// Sits between low and desktop: the game's sector-overview tier. Still the BAKED
// pipeline (cheap per-frame render so a whole sector of them stays affordable),
// but higher-res than the XR/low preset so a planet reads well at overview zoom.
// The single approached planet gets quality:'high' (desktop live shading).
const MED_PRESET = {
	baked: true,
	bres: 384,   // rtA is (bres·ATLASN)² RGBA half-float — THE biggest medium
	             // allocation: 512→52 MB, 384→29 MB per planet (ATLASN 5).
	mres: 128,
	sres: 384,
	ares: 512, // 4× less GPU than the 1024 default (see XR_PRESET note): ~48 MB
	           // worst-case vs ~192 MB — a sector of these must fit at once.
	wave: 0.1,
	pdet: 2,
	liveErosion: false,
	breakupOctaves: 10,
	chunksPerFrame: 3,
};

// a uniform array padded to the size the shader declares (see the uCs/uT1s/uT2s note below)
function padTo(array, size, fill) {
	const out = array.slice(0, size);
	while (out.length < size) out.push(fill);
	return out;
}

function disposeObject(root) {
	root.traverse(o => {
		if (o.geometry) o.geometry.dispose();
		const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
		for (const m of mats) {
			for (const k in m) { const v = m[k]; if (v && v.isTexture) v.dispose(); }
			// ShaderMaterial textures live in .uniforms, not direct props. NULL the
			// refs (do NOT dispose here — uAtlas is shared across planets and the RT
			// textures are freed via _disposables) so a material object that outlives
			// dispose() for any reason cannot pin the multi-MB DataTexture arrays.
			if (m.uniforms) for (const k in m.uniforms) {
				const u = m.uniforms[k];
				if (u && u.value && u.value.isTexture) u.value = null;
			}
			m.dispose?.();
		}
	});
}

// Atlas texture (4×4 grid, terrain.jpg) — ONLY loaded for LAVA planets, where
// real lava samples cell 10 from it (avoids a fetch on every other planet).
// Mirrors legacy planet-3rdgen's loader verbatim: NoColorSpace, mipmaps +
// anisotropy so the atlas does not alias/shimmer in XR (the 3% UV inset in
// atlasSample keeps cell bleed at bay). Cached per URL so several lava planets
// share ONE texture and ONE download.
const _atlasCache = new Map();
function getAtlas(url) {
	const src = url || "../assets/terrain.jpg";
	let tex = _atlasCache.get(src);
	if (tex) return tex;
	tex = new THREE.TextureLoader().load(src,
		t => console.log("imperial-planet/next: atlas loaded", (t.image?.width) + "x" + (t.image?.height)),
		undefined,
		() => console.error("imperial-planet/next: atlas FAILED to load:", src));
	tex.colorSpace = THREE.NoColorSpace;
	tex.generateMipmaps = true;
	tex.minFilter = THREE.LinearMipmapLinearFilter;
	tex.magFilter = THREE.LinearFilter;
	tex.anisotropy = 8;
	_atlasCache.set(src, tex);
	return tex;
}

// ── shared init-worker POOL (MULTI-WORKER, parallel) ─────────────────────────
// PERF history: (1) a brand-new module Worker per planet re-parsed the ~1 MB
// three.module.js every time → a dozen startups per system. (2) The fix collapsed
// that to ONE shared worker — but a single worker services jobs SEQUENTIALLY, so
// a system's ~6 planets queued behind each other and the LAST one waited ~3s
// (measured: worker round-trips climbed 1.5s→3.0s across 6 planets, and the
// worker round-trip is ~95% of a planet's whole build time). (3) THIS: a small
// POOL of persistent workers — reused (no per-planet startup) AND parallel (jobs
// spread across threads, so 6 planets gen concurrently instead of in series).
// Jobs are correlated by a globally-incrementing id; each job goes to the
// least-busy worker. Workers are never terminated on dispose() — they persist
// for the session and are shared across all Planet instances.
const _POOL_SIZE = (() => {
	const hc = (typeof navigator !== "undefined" && navigator.hardwareConcurrency) || 4;
	return Math.max(2, Math.min(6, hc - 1)); // leave a core for the main/render thread
})();
const _workerPool = new Map(); // key -> { workers: [{worker, jobs}], nextId }
function getPoolWorker(workerUrl, workerThreeUrl, poolSize) {
	const key = (workerUrl || "") + "|" + (workerThreeUrl || "");
	let pool = _workerPool.get(key);
	if (pool) return pool;
	// host override (opts.workerPoolSize): a phone paying 6 workers × a parsed
	// three module + 6 SIMULTANEOUS build/shader-compile spikes is a real OOM
	// contributor — mobile hosts pass 2. First caller fixes the pool's size.
	const SIZE = Math.max(1, Math.min(_POOL_SIZE, Number(poolSize) || _POOL_SIZE));
	const mkWorker = () => {
		const worker = new Worker(
			workerUrl || new URL("./planet-init-worker.js", import.meta.url),
			{ type: "module" },
		);
		const entry = { worker, jobs: new Map() };
		worker.onmessage = ev => {
			const id = ev.data && ev.data.id;
			const job = entry.jobs.get(id);
			if (job) {
				entry.jobs.delete(id);
				// the worker reports a caught exception as { id, __error } so the job
				// rejects instead of hanging (an async-onmessage throw fires no onerror).
				if (ev.data.__error) job.reject(new Error("planet-init-worker: " + ev.data.__error));
				else job.resolve(ev.data);
			}
		};
		worker.onerror = err => {
			// a worker-level error can't be attributed to one job → fail all in-flight
			for (const [, job] of entry.jobs) job.reject(err);
			entry.jobs.clear();
		};
		return entry;
	};
	const workers = [];
	for (let i = 0; i < SIZE; i++) workers.push(mkWorker());
	pool = { workers, nextId: 1 };
	_workerPool.set(key, pool);
	return pool;
}
// pick the worker with the fewest in-flight jobs (spreads a system's planets
// across threads), assign a unique id, post the spec, resolve on its reply.
function postWorkerJob(pool, spec) {
	let entry = pool.workers[0];
	for (const w of pool.workers) if (w.jobs.size < entry.jobs.size) entry = w;
	const id = pool.nextId++;
	return new Promise((resolve, reject) => {
		entry.jobs.set(id, { resolve, reject });
		entry.worker.postMessage({ id, ...spec });
	});
}

// shared across all Planet instances — see _pumpBakes for why this is global.
const _bakeGate = { frame: -1, spent: 0 };

const _UP = new THREE.Vector3(0, 1, 0);
const _pickDir = new THREE.Vector3();   // scratch: normalized pick direction
const _pickLocal = new THREE.Vector3(); // scratch: world→local pick point

export class Planet {
	/**
	 * @param {import('../common/planet-interface.js').PlanetSpec} spec
	 *        { seed, type, size, patches? }
	 * @param {object} opts  see DEFAULTS above; opts.renderer is REQUIRED by build().
	 */
	constructor(spec = {}, opts = {}) {
		this.spec = {
			seed: spec.seed ?? "Miserit",
			type: (spec.type || "EARTHLIKE").toUpperCase(),
			size: Number(spec.size ?? 18),
			patches: spec.patches || {},
		};
		// weak-hardware preset sits BETWEEN the desktop DEFAULTS and the host's
		// explicit opts, so quality:'low'/preset:'xr' flips the whole bundle while
		// any individually-passed knob still wins.
		const _q = (opts.quality || "").toLowerCase();
		const _preset = (opts.preset || "").toLowerCase();
		const _lowTier = _q === "low" || _preset === "xr";
		const _medTier = _q === "medium" || _q === "med";
		this.opts = {
			...DEFAULTS,
			...(_lowTier ? XR_PRESET : _medTier ? MED_PRESET : {}),
			...opts,
			erosion: { ...DEFAULTS.erosion, ...(opts.erosion || {}) },
		};
		this._built = false;
		this._building = false;         // true while the bake queue is draining
		this._bakeTasks = null;         // the per-frame bake queue (build() fills it)
		this._runBakeTask = null;       // closure over the bake targets/materials
		this._chunksPerFrame = 4;       // resolved in build()
		this._onBakesDone = null;       // internal reveal + resolve
		this._readyResolve = null;
		this._ready = new Promise(res => { this._readyResolve = res; });
		this._world = null;      // THREE.Group — object3d
		this._planetMesh = null;
		this._mat = null;        // sphere ShaderMaterial (per-frame uniforms live here)
		this._grid = null;       // lazy grid LineSegments
		this._buildGridFn = null;
		this._baseAt = null;     // draped-height closure (grid + tile services)
		this._dirs = null;       // per-tile unit direction (Vector3[])
		this._neighbors = null;  // per-tile adjacency
		this._boundaries = null; // raw per-tile hex boundary loops (native radius)
		this._drapedBoundaries = null; // cached draped-to-surface loops (get boundaries())
		this._N = 0;
		this._K = 1;
		this._WATER = 0.465;
		this._disposables = [];  // RTs / DataTextures to free in dispose()
		this._atmosphere = null; // Fresnel halo shell (child of object3d)
		this._atmoMat = null;    // its material (uSunDir driven per-frame)
	}

	/**
	 * Build the planet: spin the init worker, run the chunked GPU bakes, build
	 * the sphere mesh. Async so the host controls WHEN this heavy work runs (the
	 * bench deferred it to XR sessionstart; here the host simply awaits build()
	 * at the right moment). Resolves to `this`. Browser-only (needs WebGL).
	 */
	async build() {
		if (this._built) return this;
		return this._generate(false);
	}

	// The generation + GPU-bake pipeline, shared by build() (first run) and
	// applyPatches() (terraform re-bake). Spins a FRESH init worker (honoring the
	// current this.spec.patches), rebuilds the data textures / bake targets /
	// materials, builds a NEW planet mesh (hidden), and ENQUEUES the bakes into
	// this._bakeTasks — it does NOT drain them; update()'s _pumpBakes streams them
	// and _onBakesDone reveals the mesh. On a rebuild (isRebuild=true) the OLD mesh
	// stays visible until the new bakes land, then it is swapped in and the old GL
	// resources are disposed. Reuses the EXACT chunked runner + warmup path below.
	async _generate(isRebuild = false) {
		const opts = this.opts;
		const renderer = opts.renderer;
		if (!renderer || !renderer.isWebGLRenderer) {
			throw new Error(
				"imperial-planet/next: build() needs opts.renderer (the host's THREE.WebGLRenderer) for the GPU bakes.",
			);
		}

		// snapshot the CURRENT (pre-rebuild) GL resources / mesh / grid so a
		// terraform re-bake can keep them LIVE until the new bakes finish, then
		// retire them in _onBakesDone. On a first build these are empty/null.
		const prevDisposables = this._disposables;
		this._disposables = [];
		const prevMesh = isRebuild ? this._planetMesh : null;
		const prevMat = isRebuild ? this._mat : null;
		const prevGrid = isRebuild ? this._grid : null;
		const prevGridVisible = !!(prevGrid && prevGrid.visible);

		// ── derived config (the bench's module-scope consts, from opts) ──────────
		const SIMPLE_RENDERER = !!opts.simple;
		const QUALITY = (opts.quality || "").toLowerCase();
		const BAKED = !SIMPLE_RENDERER && (opts.baked != null ? !!opts.baked
			: QUALITY === "low" ? true
			: QUALITY === "high" ? false
			: false);
		const ARES = Math.max(64, Number(opts.ares || 1024));
		const ZRES = Math.max(256, Number(opts.zres || 2048));
		const BTILE = Math.max(0, Number(opts.btile ?? 0));
		const BRES = Number(opts.bres || 768);
		const MRES = Number(opts.mres || 192);
		const SRES = Number(opts.sres || 512);
		const WAVE = Number(opts.wave ?? 0.1);
		const WSMOOTH = Math.min(0.98, Math.max(0.0, Number(opts.wsmooth ?? 0.8)));
		const MAXC = Math.min(30, Math.max(1, Number(opts.maxCharts || 24)));
		const PDET = Number(opts.pdet ?? 2);
		const LIVE_EROSION = !SIMPLE_RENDERER && !BAKED && (opts.liveErosion != null ? !!opts.liveErosion : true);
		const BREAKUP = !SIMPLE_RENDERER && (opts.breakup != null ? !!opts.breakup : true);
		const BREAKUP_OCTAVES = Math.min(12, Math.max(1, Number(opts.breakupOctaves || 12)));
		const MAT_BK_OCT = Math.min(12, Math.max(1, Number(opts.matBakeOctaves ?? 12)));
		const DETAIL_TEXTURE = BREAKUP && opts.detailTexture === true;
		const DRES = Math.max(16, Number(opts.dres || 128));
		// bench: `const CHUNKS_PER_FRAME = standaloneXR ? 1 : 4;` (planet.js L1204).
		// The low/XR preset seeds chunksPerFrame=1; if a host neither used the
		// preset nor set the knob, fall back to 4 (desktop).
		const LOW_TIER = QUALITY === "low" || (opts.preset || "").toLowerCase() === "xr";
		const CHUNKS_PER_FRAME = Math.max(1, Math.floor(
			opts.chunksPerFrame != null ? Number(opts.chunksPerFrame) : (LOW_TIER ? 1 : 4)));
		this._chunksPerFrame = CHUNKS_PER_FRAME;
		// bakeSlice (off by default): short draws for weak GPUs, see DEFAULTS. When set it
		// overrides chunksPerFrame for the load bakes of this planet (btile stays the base tiling).
		const SLICE = opts.bakeSlice ? (() => {
			const o = opts.bakeSlice === true ? {} : opts.bakeSlice;
			return {
				tileSize: Math.max(8, Math.floor(Number(o.tileSize ?? 64))),
				maxTilesPerFrame: Math.max(1, Math.floor(Number(o.maxTilesPerFrame ?? 2))),
				flush: o.flush !== false,
			};
		})() : null;
		if (SLICE) this._chunksPerFrame = SLICE.maxTilesPerFrame;

		// the plane recipe, verbatim (plane.js bake mode)
		const HEXS = 0.22;
		const SQ3 = Math.sqrt(3);
		const APO = (HEXS / 2) * (SQ3 / 2);
		const WATER = 0.465;
		const PLAIN = WATER + Number(opts.free ?? 0.006);
		const OCEANH = WATER - Number(opts.basin ?? 0.13);
		const PEAKH = Number(opts.peakh || 0.065);
		const BUMPR = Number(opts.bumpr ?? 1.35);
		const BUMPS = Number(opts.bumps ?? 0.005);
		const P = {
			scale: Number(opts.erosion.scale || 0.08333),
			strength: Number(opts.erosion.strength || 0.16),
			power: Number(opts.erosion.power || 0.6),
			cell: Number(opts.erosion.cell || 1.0),
			octaves: Number(opts.erosion.octaves || 5),
			gain: Number(opts.erosion.gain || 0.5),
			lacunarity: Number(opts.erosion.lacunarity || 2.0),
		};
		const FLOOR_SHELF = WATER - Number(opts.shelfSea ?? 0.02);
		const SMK = Number(opts.smk ?? 60);
		let EROSION_ON = opts.erosionOn != null ? !!opts.erosionOn : true;
		const MBASE = Number(opts.mbase ?? 0.75);
		const MBONUS = Number(opts.mbonus ?? 0.32);
		const WATER_ON = opts.water != null ? !!opts.water : true;
		const DBG_CHART = opts.dbgChart === true;
		const ATMO = opts.atmosphere !== false;
		this._WATER = WATER;

		// ── GAS GIANTS (ADD-only) ────────────────────────────────────────────────
		// Ported from legacy planet-3rdgen: gaseous types render as painted
		// LATITUDINAL BANDS that OVERRIDE the terrain colour (no continents). The
		// band palette (g1/g2/g3 + bandFreq + atmo) is seed-derived, so every gas
		// giant looks a little different. These become compile-time GLSL consts in
		// FRAG_D (like WSMOOTH/ARES), gated by `#define USE_GAS`. The terrain/bake
		// pipeline is left untouched for gas — FRAG_D simply replaces the final
		// surface colour with the live band expression (see the USE_GAS block).
		const PT_TYPE = PLANET_TYPES[this.spec.type] || PLANET_TYPES.EARTHLIKE;
		const USE_GAS = PT_TYPE.gas === true;
		const GAS = USE_GAS ? gasPalette(this.spec.seed) : null;
		// MECHA (ADD-only): the procedural molten-crack look (dark basalt crust +
		// glowing cracks for fields, near-black volcanic rock for mountains) is
		// computed LIVE in FRAG_D. Originally authored as LAVA, but as lava it read
		// as leopard-print/fungus — kept instead as a dark tech planet type. Gated
		// so ONLY mecha planets run it; see the `#if USE_MECHA` block in FRAG_D.
		const USE_MECHA = this.spec.type === "MECHA";
		// LAVA (ADD-only): REAL lava — samples the actual lava texture (atlas cell
		// 10) for the fields and darkens the mountains to black volcanic rock; the
		// unconditional emissive term makes the texture's hot parts glow. Needs the
		// terrain atlas, so it (and ONLY it) triggers the atlas fetch below. See the
		// `#if USE_LAVA` block in FRAG_D.
		const USE_LAVA = this.spec.type === "LAVA";
		// load the 4×4 terrain atlas ONLY for lava planets (cell 10 = lava rock)
		const atlasTex = USE_LAVA ? getAtlas(opts.atlasUrl) : null;

		// ── init runs OFF the main thread (module worker) ────────────────────────
		// The heavy synchronous setup (getSphereMath/planetTerrains/generateTerrain
		// + k-means + CPU raster) runs on its own thread; we await it, then
		// reconstruct the cheap THREE objects and build the GL textures here.
		// workerUrl lets a BUNDLED host (esbuild/webpack) point at a served copy of
		// the worker — `new URL(import.meta.url)` resolves to the bundle's own path,
		// where the worker file does not exist. Standalone/static hosts omit it.
		const pool = getPoolWorker(opts.workerUrl, opts.workerThreeUrl, opts.workerPoolSize);
		const __initResult = await postWorkerJob(pool, {
			type: this.spec.type, size: this.spec.size, seed: this.spec.seed,
			patches: this.spec.patches, // honored by the initial generation (PHASE 1)
			MAXC, MRES, HEXS, PEAKH, MBASE, MBONUS,
			workerThreeUrl: opts.workerThreeUrl,
		});
		const {
			K, N, NCC, ATLASN,
			maskData, biomeData, heights, chartOfTile, dirsFlat,
			mtns, mtnStart, mtnCount, segs, segStart, segCount, chartsData,
			boundaries, neighbors,
		} = __initResult;
		if (typeof window !== 'undefined' && window.__tierLog) window.__tierLog.push({ t: Math.round(performance.now()), event: 'phase:worker-done', planet: this.spec.seed });
		const sphere = { boundaries };
		const MTN_N = Math.max(1, mtns.length / 4);
		const SEG_N = Math.max(1, segs.length / 8);
		this._N = N;
		this._K = K;
		this._neighbors = neighbors;
		this._boundaries = boundaries;
		this._drapedBoundaries = null; // invalidate the draped cache (terrain may have changed)

		// reconstruct THREE objects from the transferred plain data
		const dirs = [];
		for (let i = 0; i < N; i++) dirs.push(new THREE.Vector3(dirsFlat[i * 3], dirsFlat[i * 3 + 1], dirsFlat[i * 3 + 2]));
		this._dirs = dirs;
		const charts = chartsData.map(cd => ({
			dir: new THREE.Vector3(cd.dir[0], cd.dir[1], cd.dir[2]),
			t1: new THREE.Vector3(cd.t1[0], cd.t1[1], cd.t1[2]),
			t2: new THREE.Vector3(cd.t2[0], cd.t2[1], cd.t2[2]),
			span: cd.span, tiles: cd.tiles,
		}));
		function chartCoords(d, c) { // PLANE units
			const ct = THREE.MathUtils.clamp(d.dot(c.dir), -1, 1);
			const th = Math.acos(ct);
			const tang = d.clone().addScaledVector(c.dir, -ct);
			const l = tang.length();
			if (l < 1e-9) return [0, 0];
			tang.divideScalar(l);
			return [K * th * tang.dot(c.t1), K * th * tang.dot(c.t2)];
		}

		// ── data textures (main thread — GL) ─────────────────────────────────────
		const maskTex = new THREE.DataTexture(maskData, MRES * ATLASN, MRES * ATLASN, THREE.RGFormat, THREE.FloatType);
		maskTex.minFilter = maskTex.magFilter = THREE.LinearFilter;
		maskTex.needsUpdate = true;
		const biomeTex = new THREE.DataTexture(biomeData, MRES * ATLASN, MRES * ATLASN, THREE.RGBAFormat, THREE.FloatType);
		biomeTex.minFilter = biomeTex.magFilter = THREE.LinearFilter;
		biomeTex.needsUpdate = true;
		const segTex = new THREE.DataTexture(segs.length ? segs : new Float32Array(8), SEG_N * 2, 1, THREE.RGBAFormat, THREE.FloatType);
		segTex.minFilter = segTex.magFilter = THREE.NearestFilter;
		segTex.needsUpdate = true;
		const mtnTex = new THREE.DataTexture(mtns.length ? mtns : new Float32Array([0, 0, 0, 0]), MTN_N, 1, THREE.RGBAFormat, THREE.FloatType);
		mtnTex.minFilter = mtnTex.magFilter = THREE.NearestFilter;
		mtnTex.needsUpdate = true;
		this._disposables.push(maskTex, biomeTex, segTex, mtnTex);

		// ── GLSL (verbatim from the bench; only the config interpolations change) ─
		// Per-planet layout values are UNIFORMS (array sizes use the cap NCC_MAX):
		// baking the chart count / atlas side into the source gave every planet
		// with a new chart count its own 3 programs → a multi-second GPU compile
		// stall per planet on the Pico (2026-10-10). Now all planets of a type
		// share programs; the compile happens once per session.
		const DEFS = /* glsl */`
  uniform int uNcc, uAtlasN;
  #define NCC_MAX ${MAXC}
  #define NCC uNcc
  #define ATLASN uAtlasN
`;
		const FRAG_A = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  ${EROSION_GLSL}
  ${DEFS}
  uniform sampler2D uMask, uMtns;
  uniform float uSpans[NCC_MAX];
  uniform int uMtnStart[NCC_MAX], uMtnCount[NCC_MAX];
  uniform sampler2D uSegs;
  uniform int uSegStart[NCC_MAX], uSegCount[NCC_MAX];
  uniform float uSmK;
  uniform float uPlain, uOceanH, uWaterH, uFloorShelf;
  uniform float uBumpR, uBumps, uEroOn, uEfade, uEfloor, uEoff;
  uniform float uScale, uStrength, uPower, uCell, uGain, uLac;
  uniform int uOcts[NCC_MAX];
  uniform vec4 uUvXform;

  vec3 FractalNoise(vec2 p, float freq, int octaves, float lacunarity, float gain) {
    vec3 n = vec3(0.0);
    float nf = freq, na = 1.0;
    for (int i = 0; i < 8; i++) {
      if (i >= octaves) break;
      n += noised(p * nf) * na * vec3(1.0, nf, nf);
      na *= gain; nf *= lacunarity;
    }
    return n;
  }
  float MagnitudeSum(int octaves, float gain) {
    return (1.0 - pow(gain, float(octaves))) / (1.0 - gain);
  }
  vec2 maskAt(vec2 p, int idx, float span) {
    vec2 uv = (clamp(p / span + 0.5, 0.001, 0.999)
      + vec2(float(idx - (idx / ATLASN) * ATLASN), float(idx / ATLASN))) / float(ATLASN);
    return texture2D(uMask, uv).rg;
  }
  float baseAt(vec2 p, int idx, float span) {
    vec2 mk = maskAt(p, idx, span);
    float floorH = mix(uFloorShelf, uOceanH, mk.g);
    return floorH + (uWaterH - floorH) * smoothstep(0.10, 0.51, mk.r)
      + (uPlain - uWaterH) * smoothstep(0.49, 0.62, mk.r);
  }
  vec2 Heightmap(vec2 p, int idx, float span, out float eroMask) {
    float st = span / ${MRES.toFixed(1)};
    float b0 = baseAt(p, idx, span);
    float bx = baseAt(p + vec2(st, 0.0), idx, span);
    float by = baseAt(p + vec2(0.0, st), idx, span);
    vec3 n = vec3(b0, (bx - b0) / st, (by - b0) / st);
    float S = 0.0;
    vec2 SG = vec2(0.0);
    int start = uMtnStart[idx];
    int count = uMtnCount[idx];
    for (int j = 0; j < 512; j++) {
      if (j >= count) break;
      vec4 mt = texelFetch(uMtns, ivec2(start + j, 0), 0);
      vec2 rv = p - mt.xy;
      float dist = length(rv);
      float R = ${APO.toFixed(6)} * uBumpR;
      if (dist >= R) continue;
      float x = 1.0 - dist / R;
      float f = mt.z * x * x * (3.0 - 2.0 * x);
      vec2 g = dist > 1e-9 ? (-mt.z * 6.0 * x * (1.0 - x) / R) * rv / dist : vec2(0.0);
      float w = exp(uSmK * f);
      S += w - 1.0;
      SG += w * g;
    }
    int sStart = uSegStart[idx];
    int sCount = uSegCount[idx];
    for (int j = 0; j < 512; j++) {
      if (j >= sCount) break;
      vec4 s0 = texelFetch(uSegs, ivec2((sStart + j) * 2, 0), 0);
      vec4 s1 = texelFetch(uSegs, ivec2((sStart + j) * 2 + 1, 0), 0);
      vec2 ab = s0.zw - s0.xy;
      float L2 = max(dot(ab, ab), 1e-9);
      float t = clamp(dot(p - s0.xy, ab) / L2, 0.0, 1.0);
      vec2 rv = p - (s0.xy + ab * t);
      float dist = length(rv);
      float R = ${APO.toFixed(6)} * uBumpR;
      if (dist >= R) continue;
      float phT = mix(s1.x, s1.y, t);
      float x = 1.0 - dist / R;
      float f = phT * x * x * (3.0 - 2.0 * x);
      float shape = x * x * (3.0 - 2.0 * x);
      vec2 g = (dist > 1e-9 ? (-phT * 6.0 * x * (1.0 - x) / R) * rv / dist : vec2(0.0))
        + (t > 0.001 && t < 0.999 ? shape * (s1.y - s1.x) * ab / L2 : vec2(0.0));
      float w = exp(uSmK * f);
      S += w - 1.0;
      SG += w * g;
    }
    float denom = 1.0 + S;
    n += vec3(log(denom) / uSmK, SG / denom);
    vec3 tb = FractalNoise(p * 4.7 + vec2(13.1, 5.7), 5.0, 2, 2.0, 0.5);
    n += tb * uBumps * vec3(1.0, 4.7, 4.7);
    int oct = uEroOn > 0.5 ? uOcts[idx] : 0;
    float strength = uStrength * smoothstep(uPlain + uEfloor, uPlain + uEfade, n.x);
    vec3 h = Erosion(p, n, uScale, strength, uPower, uCell, oct, uGain, uLac);
    float eroMag = uScale * strength * MagnitudeSum(oct, uGain);
    eroMask = eroMag > 1e-9 ? h.x / eroMag : 0.0;
    return vec2(n.x + h.x + eroMag * uEoff, 0.0);
  }
  void main() {
    vec2 uvA = vUv * uUvXform.xy + uUvXform.zw;
    vec2 tile = floor(uvA * float(ATLASN));
    int idx = int(tile.y) * ATLASN + int(tile.x);
    if (idx >= NCC) { gl_FragColor = vec4(uOceanH, 0.0, 0.0, 0.0); return; }
    float span = uSpans[idx];
    vec2 p = (fract(uvA * float(ATLASN)) - 0.5) * span;
    float ero, ex0_, ex1_, ey0_, ey1_;
    vec2 h = Heightmap(p, idx, span, ero);
    float ds = 0.5 * span / ${BRES.toFixed(1)};
    vec2 hx0 = Heightmap(p - vec2(ds, 0.0), idx, span, ex0_);
    vec2 hx1 = Heightmap(p + vec2(ds, 0.0), idx, span, ex1_);
    vec2 hy0 = Heightmap(p - vec2(0.0, ds), idx, span, ey0_);
    vec2 hy1 = Heightmap(p + vec2(0.0, ds), idx, span, ey1_);
    vec3 v1 = vec3(2.0 * ds, 0.0, hx1.x - hx0.x);
    vec3 v2 = vec3(0.0, 2.0 * ds, hy1.x - hy0.x);
    vec3 normal = normalize(cross(v1, v2)).xzy;
    gl_FragColor = vec4(h.x, normal.xz, ero);
  }
`;
		const FRAG_DETAIL = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  ${EROSION_GLSL}
  ${DEFS}
  uniform float uSpans[NCC_MAX];
  vec3 breakupAt(vec2 p, float pxw) {
    vec3 bk = vec3(0.0);
    float a = 0.5, f = 2.0;
    for (int i = 0; i < ${BREAKUP_OCTAVES}; i++) {
      vec3 n = noised(p * f) * a;
      if (i < 8) bk += n;
      else bk.yz += n.yz * smoothstep(2.0, 8.0, 1.0 / (f * pxw));
      a *= 0.95; f *= 2.0;
    }
    return bk;
  }
  void main() {
    vec2 tile = floor(vUv * float(ATLASN));
    int idx = int(tile.y) * ATLASN + int(tile.x);
    if (idx >= NCC) { gl_FragColor = vec4(0.0); return; }
    float span = uSpans[idx];
    vec2 p = (fract(vUv * float(ATLASN)) - 0.5) * span;
    gl_FragColor = vec4(breakupAt(p, span / ${DRES.toFixed(1)}), 1.0);
  }
`;
		const FRAG_MAT = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  ${EROSION_GLSL}
  ${DEFS}
  #define saturate(x) clamp(x, 0.0, 1.0)
  #define CLIFF_COLOR    vec3(0.22, 0.2, 0.2)
  #define DIRT_COLOR     vec3(0.6, 0.5, 0.4)
  #define SAND_COLOR     vec3(0.8, 0.7, 0.6)
  #define WATER_COLOR       vec3(0.01, 0.04, 0.16)
  #define WATER_SHORE_COLOR vec3(0.04, 0.26, 0.42)
  uniform sampler2D uMapA, uBiome;
  uniform float uSpans[NCC_MAX];
  uniform float uWaterH, uPlain, uPDet, uBk, uWaterOn, uBasinH, uEroOn, uCShift, uEfade, uEfloor;
  uniform float uScale, uStrength, uPower, uCell, uGain, uLac;
  uniform int uOcts2[NCC_MAX];
  uniform int uEoct;
  uniform float uOutMode;
  uniform int uZoomIdx;
  uniform float uTexels;
  uniform vec4 uUvXform;

  vec2 atlasUVm(int i, vec2 p) {
    float fx = float(i - (i / ATLASN) * ATLASN);
    float fy = float(i / ATLASN);
    return (clamp(p / uSpans[i] + 0.5, 0.001, 0.999) + vec2(fx, fy)) / float(ATLASN);
  }
  vec3 breakupAt(vec2 pp, float pxw) {
    vec3 bk = vec3(0.0);
    float a = 0.5, f = 2.0;
    for (int i = 0; i < ${MAT_BK_OCT}; i++) {
      vec3 n = noised(pp * f) * a * smoothstep(2.0, 8.0, 1.0 / (f * pxw));
      if (i < 8) bk += n;
      else bk.yz += n.yz;
      a *= 0.95; f *= 2.0;
    }
    return bk;
  }

  void main() {
    vec2 uvA = vUv * uUvXform.xy + uUvXform.zw;
    int idx; vec2 p; float span, px;
    if (uZoomIdx >= 0) {
      idx = uZoomIdx;
      span = uSpans[idx];
      p = (uvA - 0.5) * span;
      px = span / uTexels;
    } else {
      vec2 tile = floor(uvA * float(ATLASN));
      idx = int(tile.y) * ATLASN + int(tile.x);
      if (idx >= NCC) { gl_FragColor = vec4(0.0); return; }
      span = uSpans[idx];
      p = (fract(uvA * float(ATLASN)) - 0.5) * span;
      px = span / uTexels;
    }

    vec4 tex = texture2D(uMapA, atlasUVm(idx, p));
    float h = tex.x;
    vec3 cn = vec3(tex.y, 0.0, tex.z);
    cn.y = sqrt(max(1.0 - dot(cn.xz, cn.xz), 0.0));
    float erosion = tex.w;
    float cov = max(abs(p.x), abs(p.y)) / span;
    float deepMix = smoothstep(0.42, 0.49, cov);
    h = mix(h, uBasinH, deepMix);
    erosion *= 1.0 - deepMix;
    cn = normalize(mix(cn, vec3(0.0, 1.0, 0.0), deepMix));

    vec3 bk = breakupAt(p, px);

    float hpix = h;
    vec2 slope = -cn.xz / max(cn.y, 1e-3);
    int octk = uOcts2[idx];
    int pdetK = uEoct - octk + int(uPDet);
    if (uEroOn > 0.5 && pdetK > 0) {
      float st = uStrength * smoothstep(uPlain + uEfloor, uPlain + uEfade, h);
      vec3 hs = vec3(h, slope);
      float freq = (1.0 / (uScale * uCell)) * pow(uLac, float(octk));
      float str = st * uScale * pow(uGain, float(octk));
      vec2 steer = slope;
      if (octk > 0) {
        float f0 = freq / uLac;
        float s0 = str / uGain;
        float sq0 = dot(steer, steer);
        vec2 is0 = steer * pow(max(sq0, 1e-12), 0.5 * (uPower - 1.0));
        steer += (Gullies(p * f0, is0 * uCell) * s0 * vec3(1.0, f0, f0)).yz;
      }
      steer += bk.yz * 0.1;
      for (int i = 0; i < 8; i++) {
        if (i >= pdetK) break;
        float lod = smoothstep(2.0, 8.0, 1.0 / (freq * px));
        if (lod > 0.001) {
          float sqrLen = dot(steer, steer);
          vec2 inputSlope = steer * pow(max(sqrLen, 1e-12), 0.5 * (uPower - 1.0));
          vec3 g = Gullies(p * freq, inputSlope * uCell) * (str * lod) * vec3(1.0, freq, freq);
          hs += g;
          steer += g.yz;
        }
        str *= uGain;
        freq *= uLac;
      }
      hpix = hs.x;
      slope = hs.yz;
    }

    vec3 nrm = normalize(vec3(1.0, -slope.x, -slope.y));
    int material = 0;
    float diff = 0.0;
    if (uWaterOn > 0.5 && h < uWaterH) {
      material = 2;
      diff = uWaterH - h;
      nrm = vec3(1.0, 0.0, 0.0);
      hpix = h;
    }
    if (material == 0) nrm = normalize(nrm + vec3(0.0, bk.z, bk.y) * 0.1 * uBk);
    float breakup = bk.x * uBk;

    float smoothness = 0.0;
    float occlusion = 1.0;
    vec3 diffuseColor = vec3(0.5);
    if (material == 0) {
      occlusion = saturate(erosion + 0.5);
      float ny = nrm.x;
      float hc = hpix + uCShift;
      diffuseColor = CLIFF_COLOR * smoothstep(0.4, 0.52, hc);
      diffuseColor = mix(diffuseColor, DIRT_COLOR, smoothstep(0.3, 0.0, occlusion - 0.2 + breakup * 1.0));
      vec3 biome = texture2D(uBiome, atlasUVm(idx, p)).rgb;
      vec3 grassMix = biome * mix(0.75, 1.2, smoothstep(0.4, 0.6, hc - erosion * 0.05 + breakup * 0.3));
      diffuseColor = mix(diffuseColor, grassMix, smoothstep(0.478 + 0.05 - 0.013, 0.478 + 0.02 - 0.013, hc + 0.01 + (occlusion - 0.8) * 0.05 - breakup * 0.02) * smoothstep(0.8, 1.0, ny + breakup * 0.1));
      diffuseColor = mix(diffuseColor, vec3(1.0), smoothstep(0.53, 0.6, hc + breakup * 0.1));
      diffuseColor = mix(diffuseColor, SAND_COLOR, smoothstep(uWaterH + 0.005 + uCShift, uWaterH + uCShift, hc + breakup * 0.01));
      diffuseColor *= 1.0 + breakup * 0.5;
    } else {
      float shore = 1.0 - smoothstep(0.035, 0.075, diff); // continental shelf: flat shallow, then a drop-off edge
      float foam = smoothstep(0.005, 0.0, diff + breakup * 0.005);
      diffuseColor = mix(WATER_COLOR, WATER_SHORE_COLOR, shore);
      diffuseColor = mix(diffuseColor, vec3(1.0), foam);
      smoothness = ${WSMOOTH.toFixed(3)};
    }

    if (uOutMode < 0.5) gl_FragColor = vec4(diffuseColor * 0.5, smoothness);
    else gl_FragColor = vec4(nrm.yz * 0.5 + 0.5, occlusion, material == 2 ? 1.0 : 0.0);
  }
`;
		const CHART_GLSL = /* glsl */`
  ${DEFS}
  uniform vec3 uCs[NCC_MAX], uT1s[NCC_MAX], uT2s[NCC_MAX];
  uniform float uSpans2[NCC_MAX];
  uniform float uK;
  vec2 chartOfC(vec3 d, vec3 c, vec3 t1, vec3 t2) {
    float ct = clamp(dot(d, c), -1.0, 1.0);
    float theta = acos(ct);
    vec3 tang = d - c * ct;
    float tl = length(tang);
    if (tl < 1e-6) return vec2(0.0);
    tang /= tl;
    return uK * theta * vec2(dot(tang, t1), dot(tang, t2));
  }
  void nearest2(vec3 d, out int best, out int second, out float bd, out float sd) {
    best = 0; second = 0; bd = -2.0; sd = -2.0;
    for (int i = 0; i < NCC; i++) {
      float dd = dot(d, uCs[i]);
      if (dd > bd) { second = best; sd = bd; best = i; bd = dd; }
      else if (dd > sd) { second = i; sd = dd; }
    }
  }
  vec2 atlasUV(int i, vec2 p) {
    float fx = float(i - (i / ATLASN) * ATLASN);
    float fy = float(i / ATLASN);
    return (clamp(p / uSpans2[i] + 0.5, 0.001, 0.999) + vec2(fx, fy)) / float(ATLASN);
  }
`;
		const VERT_D = /* glsl */`
  varying vec3 vDir;
  varying vec3 vWorldPosition;
  uniform sampler2D uMapA;
  uniform float uWaterH, uWaterOn, uBasinH;
  ${CHART_GLSL}
  void main() {
    vec3 d = normalize(position);
    int best, second; float bd, sd;
    nearest2(d, best, second, bd, sd);
    vec2 p = chartOfC(d, uCs[best], uT1s[best], uT2s[best]);
    float h = texture2D(uMapA, atlasUV(best, p)).x;
    float cov = max(abs(p.x), abs(p.y)) / uSpans2[best];
    h = mix(h, uBasinH, smoothstep(0.42, 0.49, cov));
    if (uWaterOn > 0.5) h = max(h, uWaterH);
    vec3 wp = d * (1.0 + (h - uWaterH) / uK);
    vDir = wp;
    vWorldPosition = (modelMatrix * vec4(wp, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(wp, 1.0);
  }
`;
		const FRAG_D = /* glsl */`
  precision highp float;
${BAKED ? `  layout(location = 0) out highp vec4 outFragColor;
  #define gl_FragColor outFragColor
` : ""}  ${EROSION_GLSL}
  ${CHART_GLSL}
  #define USE_DETAIL_TEXTURE ${DETAIL_TEXTURE ? 1 : 0}
  #define USE_BREAKUP ${BREAKUP ? 1 : 0}
  #define USE_LIVE_EROSION ${LIVE_EROSION ? 1 : 0}
  #define USE_WAVES ${WAVE > 0 ? 1 : 0}
  #define USE_BAKED ${BAKED ? 1 : 0}
  #define USE_GAS ${USE_GAS ? 1 : 0}
  #define GAS_G1 vec3(${GAS ? GAS.g1 : "0.0,0.0,0.0"})
  #define GAS_G2 vec3(${GAS ? GAS.g2 : "0.0,0.0,0.0"})
  #define GAS_G3 vec3(${GAS ? GAS.g3 : "0.0,0.0,0.0"})
  #define GAS_BANDFREQ ${(GAS ? Number(GAS.bandFreq) : 1.0).toFixed(4)}
  #define USE_MECHA ${USE_MECHA ? 1 : 0}
  #define USE_LAVA ${USE_LAVA ? 1 : 0}
  #define LAVA_BASALT vec3(0.05, 0.04, 0.04)
  #define LAVA_HOT    vec3(1.0, 0.35, 0.05)
  #if USE_LAVA
  // 4x4 terrain atlas (terrain.jpg). texture2D works in both glslVersions: three
  // compiles every non-raw ShaderMaterial to "#version 300 es" and injects a
  // "define texture2D texture" compat macro. Ported from legacy planet-3rdgen.
  uniform sampler2D uAtlas;
  vec3 atlasSample(float cell, vec2 uv) {
    cell = floor(cell + 0.5);
    float cols = 4.0, rows = 4.0;
    float cx = mod(cell, cols), cy = floor(cell / cols);
    vec2 iuv = uv * 0.94 + 0.03;
    vec2 a = vec2(cx / cols + iuv.x / cols, 1.0 - (cy / rows + (1.0 - iuv.y) / rows));
    return texture2D(uAtlas, a).rgb;
  }
  #endif
  #define saturate(x) clamp(x, 0.0, 1.0)
  #define sq(x) ((x)*(x))
  #define CLIFF_COLOR    vec3(0.22, 0.2, 0.2)
  #define DIRT_COLOR     vec3(0.6, 0.5, 0.4)
  #define GRASS_COLOR1   vec3(0.15, 0.3, 0.1)
  #define GRASS_COLOR2   vec3(0.4, 0.5, 0.2)
  #define SAND_COLOR     vec3(0.8, 0.7, 0.6)
  #define WATER_COLOR       vec3(0.01, 0.04, 0.16)
  #define WATER_SHORE_COLOR vec3(0.04, 0.26, 0.42)
  #define SUN_COLOR (vec3(1.0, 0.98, 0.95) * 2.0)
  #define AMBIENT_COLOR (vec3(0.3, 0.5, 0.7) * 0.1)
  uniform sampler2D uMapA, uBiome, uDetail;
  #if USE_BAKED
  precision highp sampler2DArray;
  uniform sampler2DArray uMatC, uMatN;
  #endif
  uniform sampler2D uZoomC, uZoomN;
  uniform int uZoomChart;
  // atlas texel counts as UNIFORMS, not compile-time constants: planets of
  // different atlas sizes (the game's low 256 / close-up 512 tiers) then share
  // ONE program — baking ARES into the source made every close-up rebuild
  // compile new programs on the Pico: 7 s without a frame (2026-10-10).
  uniform float uAres, uZres;
  uniform int uBkStart[NCC_MAX];
  uniform vec3 uSunDir;
  uniform mat3 uWorldRotation;
  uniform float uWaterH, uPlain, uPDet, uTime, uWave, uBk, uWaterOn, uBasinH, uEroOn, uCShift, uEfade, uEfloor, uExposure, uSaturation;
  uniform float uDbgChart;
  uniform float uScale, uStrength, uPower, uCell, uGain, uLac;
  uniform int uOcts2[NCC_MAX];
  uniform int uEoct;
  varying vec3 vDir;
  varying vec3 vWorldPosition;

  vec3 SkyColor(vec3 rd, vec3 sun) {
    float costh = dot(rd, sun);
    return AMBIENT_COLOR * PI * (1.0 - abs(costh) * 0.8);
  }
  vec3 Tonemap_ACES(vec3 x) {
    const float a = 2.51, b = 0.03, c = 2.43, d = 0.59, e = 0.14;
    return (x * (a * x + b)) / (x * (c * x + d) + e);
  }
  float pow5(float x) { float x2 = x * x; return x2 * x2 * x; }
  float D_GGX(float linearRoughness, float NoH, const vec3 h) {
    float oneMinusNoHSquared = 1.0 - NoH * NoH;
    float a = NoH * linearRoughness;
    float k = linearRoughness / (oneMinusNoHSquared + a * a);
    return k * k * (1.0 / PI);
  }
  float V_SmithGGXCorrelated(float linearRoughness, float NoV, float NoL) {
    float a2 = linearRoughness * linearRoughness;
    float GGXV = NoL * sqrt((NoV - a2 * NoV) * NoV + a2);
    float GGXL = NoV * sqrt((NoL - a2 * NoL) * NoL + a2);
    return 0.5 / (GGXV + GGXL);
  }
  vec3 F_Schlick3(const vec3 f0, float VoH) {
    return f0 + (vec3(1.0) - f0) * pow5(1.0 - VoH);
  }
  float F_Schlick1(float f0, float f90, float VoH) {
    return f0 + (f90 - f0) * pow5(1.0 - VoH);
  }
  float Fd_Burley(float linearRoughness, float NoV, float NoL, float LoH) {
    float f90 = 0.5 + 2.0 * linearRoughness * LoH * LoH;
    return F_Schlick1(1.0, f90, NoL) * F_Schlick1(1.0, f90, NoV) * (1.0 / PI);
  }
  float Fd_Lambert() { return 1.0 / PI; }
  vec3 Shade(vec3 diffuse, vec3 f0, float smoothness, vec3 n, vec3 v, vec3 l, vec3 lc) {
    vec3 h = normalize(v + l);
    float NoV = abs(dot(n, v)) + 1e-5;
    float NoL = saturate(dot(n, l));
    float NoH = saturate(dot(n, h));
    float LoH = saturate(dot(l, h));
    float roughness = 1.0 - smoothness;
    float linearRoughness = roughness * roughness;
    float D = D_GGX(linearRoughness, NoH, h);
    float V = V_SmithGGXCorrelated(linearRoughness, NoV, NoL);
    vec3 F = F_Schlick3(f0, LoH);
    vec3 Fd = diffuse * Fd_Burley(linearRoughness, NoV, NoL, LoH);
    return (Fd + (D * V) * F) * lc * NoL;
  }
  vec3 rippleAt(vec2 pp, float t) {
    return noised(pp * 64.0 + vec2(t * 0.15, t * 0.06))
      + noised(pp * 29.0 - vec2(t * 0.07, t * 0.11)) * 0.7;
  }
  vec3 breakupAt(vec2 pp, float pxw) {
    vec3 bk = vec3(0.0);
    float a = 0.5, f = 2.0;
    for (int i = 0; i < ${BREAKUP_OCTAVES}; i++) {
      vec3 n = noised(pp * f) * a;
      if (i < 8) bk += n;
      else bk.yz += n.yz * smoothstep(2.0, 8.0, 1.0 / (f * pxw));
      a *= 0.95; f *= 2.0;
    }
    return bk;
  }

  void main() {
    vec3 rhat = normalize(vDir);
    int best, second; float bdot, sdot;
    nearest2(rhat, best, second, bdot, sdot);
    vec2 p = chartOfC(rhat, uCs[best], uT1s[best], uT2s[best]);
    vec2 p2 = chartOfC(rhat, uCs[second], uT1s[second], uT2s[second]);
    float wmix = NCC > 1 ? 0.5 * (1.0 - smoothstep(0.0, 0.25, bdot - sdot)) : 0.0;
    vec3 e1 = normalize(uT1s[best] - rhat * dot(uT1s[best], rhat));
    vec3 e2 = normalize(uT2s[best] - rhat * dot(uT2s[best], rhat));

  #if USE_BAKED
    vec2 luv = clamp(p / uSpans2[best] + 0.5, 0.0005, 0.9995);
    vec4 cs = texture(uMatC, vec3(luv, float(best)));
    vec4 nb = texture(uMatN, vec3(luv, float(best)));
    vec2 nlB = nb.xy * 2.0 - 1.0;
    vec3 normal = normalize(rhat * sqrt(max(1.0 - dot(nlB, nlB), 0.0)) + e1 * nlB.x + e2 * nlB.y);
    float occlusion = nb.z;
    if (wmix > 0.001) {
      vec2 luv2raw = p2 / uSpans2[second] + 0.5;
      float cov2 = 1.0 - smoothstep(0.42, 0.49, max(abs(luv2raw.x - 0.5), abs(luv2raw.y - 0.5)));
      float wz = wmix * cov2;
      if (wz > 0.001) {
        vec2 luv2 = clamp(luv2raw, 0.0005, 0.9995);
        vec4 cs2 = texture(uMatC, vec3(luv2, float(second)));
        vec4 nb2 = texture(uMatN, vec3(luv2, float(second)));
        float agree = 1.0 - clamp(length((cs.rgb - cs2.rgb) * 2.0) * 2.0, 0.0, 1.0);
        wz *= agree;
        cs = mix(cs, cs2, wz);
        occlusion = mix(occlusion, nb2.z, wz);
        vec3 e1s = normalize(uT1s[second] - rhat * dot(uT1s[second], rhat));
        vec3 e2s = normalize(uT2s[second] - rhat * dot(uT2s[second], rhat));
        vec2 nlS = nb2.xy * 2.0 - 1.0;
        vec3 nS = normalize(rhat * sqrt(max(1.0 - dot(nlS, nlS), 0.0)) + e1s * nlS.x + e2s * nlS.y);
        normal = normalize(mix(normal, nS, wz));
      }
    }
    vec3 diffuseColor = cs.rgb * 2.0;
    float smoothness = cs.a;
    #if USE_WAVES
    if (nb.w > 0.5 && uWave > 0.0) {
      vec3 wv = vec3(0.0);
      if (wmix < 0.999) wv += rippleAt(p, uTime) * (1.0 - wmix);
      if (wmix > 0.001) wv += rippleAt(p2, uTime) * wmix;
      normal = normalize(normal + (e1 * wv.y + e2 * wv.z) * uWave);
    }
    #endif
    #if USE_BREAKUP
    if (nb.w < 0.5) {
      float pxb = max(fwidth(p.x), fwidth(p.y));
      float ta = uSpans2[best] / (best == uZoomChart ? uZres : uAres);
      int k0 = uBkStart[best];
      float a = 0.5 * pow(0.95, float(k0));
      float f = exp2(float(k0 + 1));
      vec3 bkL = vec3(0.0);
      for (int i = 0; i < 12; i++) {
        if (i + k0 >= 12) break;
        float wS = smoothstep(2.0, 8.0, 1.0 / (f * pxb));
        if (wS < 0.001) break;
        float w = max(wS - smoothstep(2.0, 8.0, 1.0 / (f * ta)), 0.0);
        if (w > 0.001) {
          vec3 n = noised(p * f) * a * w;
          if (i + k0 < 8) bkL += n;
          else bkL.yz += n.yz;
        }
        a *= 0.95; f *= 2.0;
      }
      normal = normalize(normal + (e1 * bkL.z + e2 * bkL.y) * 0.1 * uBk);
      diffuseColor *= 1.0 + bkL.x * uBk * 0.5;
    }
    #endif
    vec3 f0 = vec3(0.04);
  #else
    vec4 tex = texture2D(uMapA, atlasUV(best, p));
    float h = tex.x;
    vec3 cn = vec3(tex.y, 0.0, tex.z);
    cn.y = sqrt(max(1.0 - dot(cn.xz, cn.xz), 0.0));
    float erosion = tex.w;
    float cov = max(abs(p.x), abs(p.y)) / uSpans2[best];
    float deepMix = smoothstep(0.42, 0.49, cov);
    h = mix(h, uBasinH, deepMix);
    erosion *= 1.0 - deepMix;
    cn = normalize(mix(cn, vec3(0.0, 1.0, 0.0), deepMix));
    float px = max(fwidth(p.x), fwidth(p.y));
    float px2 = max(fwidth(p2.x), fwidth(p2.y));

    vec3 bk = vec3(0.0);
  #if USE_BREAKUP
    #if USE_DETAIL_TEXTURE
    if (wmix < 0.999) bk += texture2D(uDetail, atlasUV(best, p)).rgb * (1.0 - wmix);
    if (wmix > 0.001) bk += texture2D(uDetail, atlasUV(second, p2)).rgb * wmix;
    #else
    if (wmix < 0.999) bk += breakupAt(p, px) * (1.0 - wmix);
    if (wmix > 0.001) bk += breakupAt(p2, px2) * wmix;
    #endif
  #endif

    float hpix = h;
    vec2 slope = -cn.xz / max(cn.y, 1e-3);
  #if USE_LIVE_EROSION
    int octk = uOcts2[best];
    int pdetK = uEoct - octk + int(uPDet);
    if (uEroOn > 0.5 && pdetK > 0) {
      float st = uStrength * smoothstep(uPlain + uEfloor, uPlain + uEfade, h);
      vec3 hs = vec3(h, slope);
      float freq = (1.0 / (uScale * uCell)) * pow(uLac, float(octk));
      float str = st * uScale * pow(uGain, float(octk));
      vec2 steer = slope;
      if (octk > 0) {
        float f0 = freq / uLac;
        float s0 = str / uGain;
        float sq0 = dot(steer, steer);
        vec2 is0 = steer * pow(max(sq0, 1e-12), 0.5 * (uPower - 1.0));
        steer += (Gullies(p * f0, is0 * uCell) * s0 * vec3(1.0, f0, f0)).yz;
      }
      steer += bk.yz * 0.1;
      for (int i = 0; i < 8; i++) {
        if (i >= pdetK) break;
        float lod = smoothstep(2.0, 8.0, 1.0 / (freq * px));
        if (lod > 0.001) {
          float sqrLen = dot(steer, steer);
          vec2 inputSlope = steer * pow(max(sqrLen, 1e-12), 0.5 * (uPower - 1.0));
          vec3 g = Gullies(p * freq, inputSlope * uCell) * (str * lod) * vec3(1.0, freq, freq);
          hs += g;
          steer += g.yz;
        }
        str *= uGain;
        freq *= uLac;
      }
      hpix = hs.x;
      slope = hs.yz;
    }
  #endif

    vec3 normal = normalize(rhat - e1 * slope.x - e2 * slope.y);

    int material = 0;
    float diff = 0.0;
    if (uWaterOn > 0.5 && h < uWaterH) {
      material = 2;
      diff = uWaterH - h;
      normal = rhat;
      hpix = h;
    #if USE_WAVES
      if (uWave > 0.0) {
        vec3 wv = vec3(0.0);
        if (wmix < 0.999) wv += rippleAt(p, uTime) * (1.0 - wmix);
        if (wmix > 0.001) wv += rippleAt(p2, uTime) * wmix;
        normal = normalize(normal + (e1 * wv.y + e2 * wv.z) * uWave);
      }
    #endif
    }
    normal = normalize(normal + (e1 * bk.z + e2 * bk.y) * 0.1 * uBk);
    float breakup = bk.x * uBk;

    vec3 f0 = vec3(0.04);
    float smoothness = 0.0;
    float occlusion = 1.0;
    vec3 diffuseColor = vec3(0.5);
    if (material == 0) {
      occlusion = saturate(erosion + 0.5);
      float ny = dot(normal, rhat);
      float hc = hpix + uCShift;
      diffuseColor = CLIFF_COLOR * smoothstep(0.4, 0.52, hc);
      diffuseColor = mix(diffuseColor, DIRT_COLOR, smoothstep(0.3, 0.0, occlusion - 0.2 + breakup * 1.0));
      vec3 biome = texture2D(uBiome, atlasUV(best, p)).rgb;
      vec3 grassMix = biome * mix(0.75, 1.2, smoothstep(0.4, 0.6, hc - erosion * 0.05 + breakup * 0.3));
      diffuseColor = mix(diffuseColor, grassMix, smoothstep(0.478 + 0.05 - 0.013, 0.478 + 0.02 - 0.013, hc + 0.01 + (occlusion - 0.8) * 0.05 - breakup * 0.02) * smoothstep(0.8, 1.0, ny + breakup * 0.1));
      diffuseColor = mix(diffuseColor, vec3(1.0), smoothstep(0.53, 0.6, hc + breakup * 0.1));
      diffuseColor = mix(diffuseColor, SAND_COLOR, smoothstep(uWaterH + 0.005 + uCShift, uWaterH + uCShift, hc + breakup * 0.01));
      diffuseColor *= 1.0 + breakup * 0.5;
    } else {
      float shore = 1.0 - smoothstep(0.035, 0.075, diff); // continental shelf: flat shallow, then a drop-off edge
      float foam = smoothstep(0.005, 0.0, diff + breakup * 0.005);
      diffuseColor = mix(WATER_COLOR, WATER_SHORE_COLOR, shore);
      diffuseColor = mix(diffuseColor, vec3(1.0), foam);
      smoothness = ${WSMOOTH.toFixed(3)};
    }
  #endif

  #if USE_GAS
    // GAS GIANT: latitudinal painted bands, computed LIVE in the planet's LOCAL
    // frame (vDir is pre-model local geometry, so Y is the polar axis and the
    // bands stay pinned to the poles). This REPLACES the terrain/biome/atlas
    // colour — gas giants have no continents, so the surface is a flat sphere
    // (radial normal, no erosion contribution). Ported verbatim from legacy
    // planet-3rdgen; legacy's value-noise vn (range 0..1) is adapted to next's
    // existing gradient-noise helper noised(...).x (signed), remapped to 0..1.
    {
      vec3 gp = normalize(vDir);
      float glat = asin(clamp(gp.y, -1.0, 1.0));
      float glon = atan(gp.z, gp.x);
      float wob = (noised(vec2(glat * 6.0, glon * 2.0)).x * 0.5 + 0.5) * 1.6;
      float band = sin(glat * GAS_BANDFREQ + wob) * 0.5 + 0.5;
      // STORM (Great-Red-Spot-like): a large, RARE oval swirl. Low frequencies
      // yield ~1-2 blobs per giant; longitude is scaled below latitude so the
      // blob stretches into an oval riding the bands, and swirl warps it. The
      // threshold is lowered (vs the old faint 0.75-0.95) so storms clearly show.
      float swirl = noised(vec2(glon * 1.5, glat * 4.0)).x * 0.6;
      float sblob = noised(vec2(glon * 1.1 + swirl, glat * 3.0 + 2.7)).x * 0.5 + 0.5;
      float storm = smoothstep(0.52, 0.80, sblob);
      // a finer disturbance inside the blob gives it a dark swirling core.
      float storm2 = smoothstep(0.55, 0.90, noised(vec2(glon * 3.0 + swirl, glat * 7.0)).x * 0.5 + 0.5) * storm;
      // the storm oval reads bright (toward g3) with a darker rotating core.
      vec3 stormCol = mix(GAS_G3, GAS_G1 * 0.55, storm2);
      // legacy applied pow(terr, 2.2) before lighting (sRGB palette → linear);
      // next lights in linear then does the output gamma, so decode here too.
      diffuseColor = pow(mix(mix(GAS_G1, GAS_G2, band), stormCol, storm), vec3(2.2));
      normal = rhat;      // flat sphere — no continents / erosion
      occlusion = 1.0;
      smoothness = 0.0;
    }
  #endif

  #if USE_MECHA
    // MECHA: procedural molten rock (the repurposed crack look), NOT a flat
    // orange field. This REPLACES the terrain colour but KEEPS the terrain
    // normal/occlusion (so mountains still read as 3D relief, unlike the flat gas
    // sphere). Two looks, blended by height:
    //   FIELD tiles → dark basalt crust with thin bright GLOWING cracks,
    //   MOUNTAIN tiles → near-black volcanic rock, no cracks, no snow-white.
    // The unconditional emissive term (below) then lights up the bright cracks.
    {
      // height proxy: VERT_D displaces to length(vDir)=1+(h-uWaterH)/uK, so h is
      // recoverable here in BOTH the baked and live paths. hc matches the
      // terrain path's hpix + uCShift band (field ~0.478, mountains >0.5).
      float hc = uWaterH + (length(vDir) - 1.0) * uK + uCShift;
      float mtnMask = smoothstep(0.49, 0.53, hc);   // 0 = field, 1 = mountain
      // ridged noise → thin bright channels between dark plates; a slow uTime
      // drift makes the cracks shimmer subtly. Two octaves for finer branching.
      float n1 = noised(p * 55.0 + vec2(uTime * 0.01, 0.0)).x * 0.5 + 0.5;
      float n2 = noised(p * 130.0 - vec2(0.0, uTime * 0.008)).x * 0.5 + 0.5;
      float ridge = 1.0 - abs(2.0 * n1 - 1.0);
      float crack = smoothstep(0.74, 0.92, ridge) + smoothstep(0.82, 0.96, n2) * 0.5;
      crack = clamp(crack, 0.0, 1.0);
      vec3 lavaField = mix(LAVA_BASALT, LAVA_HOT, crack);
      vec3 lavaMtn   = LAVA_BASALT * 0.7;   // near-black volcanic rock, no glow
      diffuseColor = mix(lavaField, lavaMtn, mtnMask);
      smoothness = 0.0;
    }
  #endif

  #if USE_LAVA
    // LAVA: REAL lava — the actual lava-rock texture (atlas cell 10) for the
    // fields, near-black volcanic rock for the mountains (matching the mecha
    // darkening). REPLACES the terrain colour but KEEPS the terrain
    // normal/occlusion so mountains still read as 3D relief. next has no per-hex
    // vHexUV like legacy, so the surface is tiled by lat/lon (continuous over
    // the sphere; a faint seam at the antimeridian + pole pinch, acceptable for
    // rock). The unconditional emissive term below makes the texture's hot
    // (red-dominant) parts self-illuminate — same as legacy's g_emc.
    {
      float hc = uWaterH + (length(vDir) - 1.0) * uK + uCShift;
      float mtnMask = smoothstep(0.49, 0.53, hc);   // 0 = field, 1 = mountain
      vec3 gp = normalize(vDir);
      float llat = asin(clamp(gp.y, -1.0, 1.0));     // [-pi/2, pi/2]
      float llon = atan(gp.z, gp.x);                 // [-pi, pi]
      // to [0,1] then tile ~12× around / ~6× pole-to-pole (roughly square cells)
      vec2 luv = fract(vec2(llon / 6.2831853 + 0.5, llat / 3.1415927 + 0.5) * vec2(12.0, 6.0));
      vec3 lavaField = atlasSample(10.0, luv);       // cell 10 = lava rock
      vec3 lavaMtn   = LAVA_BASALT * 0.7;            // near-black volcanic rock
      diffuseColor = mix(lavaField, lavaMtn, mtnMask);
      smoothness = 0.0;
    }
  #endif

    vec3 rd = normalize(vWorldPosition - cameraPosition);
    vec3 sun = uSunDir;
    vec3 worldNormal = normalize(uWorldRotation * normal);
    vec3 color = diffuseColor * SkyColor(worldNormal, sun) * occlusion * Fd_Lambert();
    color += Shade(diffuseColor, f0, smoothness, worldNormal, -rd, sun, SUN_COLOR);
    color += diffuseColor * SUN_COLOR * (dot(worldNormal, sun * vec3(1.0, -1.0, 1.0)) * 0.5 + 0.5) * Fd_Lambert() / PI;
    vec3 r = reflect(rd, worldNormal);
    color += SkyColor(r, sun) * 4.0 * (1.0 - exp(-10.0 * sq(smoothness))) * F_Schlick3(f0, dot(-rd, worldNormal));
    // LAVA GLOWS (ported from legacy planet-3rdgen): a strongly red-dominant
    // surface colour self-illuminates. Keyed on COLOUR, not type, so any lava /
    // glowing-crack surface adds emission. Added in linear light before the
    // tonemap, matching legacy's totalEmissiveRadiance += g_emc.
    color += diffuseColor * smoothstep(0.10, 0.35, diffuseColor.r - diffuseColor.g * 1.5) * 2.0;
    if (uSaturation != 1.0) color = mix(vec3(dot(color, vec3(0.2126, 0.7152, 0.0722))), color, uSaturation); // host saturation dial (1.0 = unchanged)
    color = Tonemap_ACES(color * uExposure); // host exposure dial (1.0 = unchanged)
    color = pow(color, vec3(1.0 / 2.2));
    if (uDbgChart > 0.5) {
      float h = fract(float(best) * 0.61803);
      vec3 tint = clamp(abs(fract(h + vec3(0.0, 0.333, 0.667)) * 6.0 - 3.0) - 1.0, 0.0, 1.0);
      color = mix(color, tint, 0.55);
      if (wmix > 0.02) color = mix(color, vec3(1.0), wmix);
    }
    gl_FragColor = vec4(color, 1.0);
  }
`;

		// ── bake targets + uniforms (unchanged from the bench) ───────────────────
		const rtA = new THREE.WebGLRenderTarget(BRES * ATLASN, BRES * ATLASN, {
			type: opts.floatTextures && renderer.extensions.has("OES_texture_float_linear")
				? THREE.FloatType : THREE.HalfFloatType,
			minFilter: THREE.LinearFilter,
			magFilter: THREE.LinearFilter,
			depthBuffer: false,
		});
		this._disposables.push(rtA);
		const octs = charts.map(c => Math.min(P.octaves,
			Math.max(1, Math.floor(Math.log2(Math.max((P.scale * P.cell * BRES) / (8 * c.span), 1))) + 1)));
		const bakeUniforms = {
			uMask: { value: maskTex }, uMtns: { value: mtnTex },
			uSegs: { value: segTex },
			uSpans: { value: charts.map(c => c.span) },
			uMtnStart: { value: Array.from(mtnStart) }, uMtnCount: { value: Array.from(mtnCount) },
			uSegStart: { value: Array.from(segStart) }, uSegCount: { value: Array.from(segCount) },
			uSmK: { value: SMK },
			uPlain: { value: PLAIN }, uOceanH: { value: OCEANH }, uWaterH: { value: WATER },
			uFloorShelf: { value: FLOOR_SHELF },
			uBumpR: { value: BUMPR },
			uEfade: { value: Number(opts.efade ?? 0.06) },
			uEfloor: { value: Number(opts.efloor ?? 0.006) },
			uEoff: { value: Number(opts.eoff ?? -0.5) },
			uBumps: { value: EROSION_ON ? BUMPS : 0 },
			uScale: { value: P.scale }, uStrength: { value: P.strength }, uPower: { value: P.power },
			uCell: { value: P.cell }, uGain: { value: P.gain }, uLac: { value: P.lacunarity },
			uOcts: { value: octs },
			uEroOn: { value: EROSION_ON ? 1 : 0 }, uNcc: { value: NCC }, uAtlasN: { value: ATLASN },
			uUvXform: { value: new THREE.Vector4(1, 1, 0, 0) },
		};
		const bakeScene = new THREE.Scene();
		const bakeCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
		const bakeQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), null);
		bakeScene.add(bakeQuad);
		// LEAK FIX: the bake quad lives in this local bakeScene, NOT in _world, so
		// disposeObject(_world) never frees it — every planet build leaked one
		// PlaneGeometry (measured: renderer.info.memory.geometries climbed +1 per
		// build/dispose cycle). Track it so dispose() frees it with everything else.
		this._disposables.push(bakeQuad.geometry);
		const BAKE_VERT = "varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }";
		const matA = new THREE.ShaderMaterial({ vertexShader: BAKE_VERT, fragmentShader: FRAG_A, uniforms: bakeUniforms });

		let detailTexture = rtA.texture;
		let detailRT = null;
		if (DETAIL_TEXTURE) {
			detailRT = new THREE.WebGLRenderTarget(DRES * ATLASN, DRES * ATLASN, {
				type: THREE.HalfFloatType,
				minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false,
			});
			bakeQuad.material = new THREE.ShaderMaterial({
				vertexShader: BAKE_VERT, fragmentShader: FRAG_DETAIL,
				uniforms: { uSpans: { value: charts.map(c => c.span) }, uNcc: { value: NCC }, uAtlasN: { value: ATLASN } },
			});
			const hostRT = renderer.getRenderTarget();
			renderer.setRenderTarget(detailRT);
			renderer.render(bakeScene, bakeCam);
			renderer.setRenderTarget(hostRT);
			bakeQuad.material.dispose();
			detailTexture = detailRT.texture;
			this._disposables.push(detailRT);
		}

		// material bake (BAKED): color + normal atlases as texture arrays
		let rtMatC = null, rtMatN = null;
		if (BAKED) {
			const mkArr = () => {
				const rt = new THREE.WebGLArrayRenderTarget(ARES, ARES, NCC, { depthBuffer: false });
				rt.texture.format = THREE.RGBAFormat;
				rt.texture.type = THREE.UnsignedByteType;
				rt.texture.minFilter = THREE.LinearFilter;
				rt.texture.magFilter = THREE.LinearFilter;
				rt.texture.generateMipmaps = false;
				return rt;
			};
			rtMatC = mkArr();
			rtMatN = mkArr();
			this._disposables.push(rtMatC, rtMatN);
			// pre-clear every layer NOW (lazy first-touch allocations are hitches)
			const hostRT = renderer.getRenderTarget();
			for (const rt of [rtMatC, rtMatN]) {
				for (let k = 0; k < NCC; k++) {
					renderer.setRenderTarget(rt, k);
					renderer.clear(true, false, false);
				}
			}
			renderer.setRenderTarget(hostRT);
		}
		const matBakeUniforms = {
			uMapA: { value: rtA.texture },
			uBiome: { value: biomeTex },
			uSpans: { value: charts.map(c => c.span) },
			uOcts2: { value: octs },
			uEoct: { value: P.octaves },
			uPDet: { value: PDET },
			uEroOn: bakeUniforms.uEroOn,
			uPlain: { value: PLAIN }, uWaterH: { value: WATER },
			uCShift: { value: 0.478 - PLAIN },
			uEfade: bakeUniforms.uEfade, uEfloor: bakeUniforms.uEfloor,
			uBasinH: { value: OCEANH },
			uWaterOn: { value: WATER_ON ? 1 : 0 },
			uScale: { value: P.scale }, uStrength: { value: P.strength }, uPower: { value: P.power },
			uCell: { value: P.cell }, uGain: { value: P.gain }, uLac: { value: P.lacunarity },
			uBk: { value: BREAKUP ? 1 : 0 },
			uOutMode: { value: 0 },
			uZoomIdx: { value: -1 },
			uTexels: { value: ARES }, uNcc: { value: NCC }, uAtlasN: { value: ATLASN },
			uUvXform: { value: new THREE.Vector4(1, 1, 0, 0) },
		};
		const matMat = BAKED ? new THREE.ShaderMaterial({ vertexShader: BAKE_VERT, fragmentShader: FRAG_MAT, uniforms: matBakeUniforms }) : null;

		// ── the planet mesh ──────────────────────────────────────────────────────
		const geo = new THREE.SphereGeometry(1, SRES, SRES / 2);
		const mat = new THREE.ShaderMaterial({
			vertexShader: VERT_D,
			fragmentShader: FRAG_D,
			uniforms: {
				uMapA: { value: rtA.texture },
				uBiome: { value: biomeTex },
				uDetail: { value: detailTexture },
				uMatC: { value: rtMatC ? rtMatC.texture : null },
				uMatN: { value: rtMatN ? rtMatN.texture : null },
				// zoom refinement removed — sample the base atlas, uZoomChart stays -1
				uZoomC: { value: rtA.texture },
				uZoomN: { value: rtA.texture },
				uZoomChart: { value: -1 },
				uAres: { value: ARES },
				uNcc: { value: NCC }, uAtlasN: { value: ATLASN },
				uZres: { value: ZRES },
				// Every per-chart array is padded to the shader's declared size NCC_MAX: three uploads
				// as many elements as the GPU reports for the uniform, and the Pico's GPU reports the
				// full declared size — with charts.length < NCC_MAX it read past the array's end,
				// threw inside the render loop and the headset went black (2026-10-11, user report).
				uBkStart: { value: padTo(charts.map(c => Math.max(0, Math.ceil(Math.log2(ARES / (8 * c.span))) - 1)), MAXC, 0) },
				uCs: { value: padTo(charts.map(c => c.dir), MAXC, new THREE.Vector3(0, 0, 1)) },
				uT1s: { value: padTo(charts.map(c => c.t1), MAXC, new THREE.Vector3(1, 0, 0)) },
				uT2s: { value: padTo(charts.map(c => c.t2), MAXC, new THREE.Vector3(0, 1, 0)) },
				uSpans2: { value: padTo(charts.map(c => c.span), MAXC, 1) },
				uK: { value: K },
				uWaterH: { value: WATER }, uPlain: { value: PLAIN },
				uCShift: { value: 0.478 - PLAIN },
				uEfade: bakeUniforms.uEfade,
				uEfloor: bakeUniforms.uEfloor,
				uBasinH: { value: OCEANH },
				uWaterOn: { value: WATER_ON ? 1 : 0 },
				uScale: { value: P.scale }, uStrength: { value: P.strength }, uPower: { value: P.power },
				uCell: { value: P.cell }, uGain: { value: P.gain }, uLac: { value: P.lacunarity },
				uOcts2: { value: octs },
				uEoct: { value: P.octaves },
				uEroOn: bakeUniforms.uEroOn,
				uSunDir: { value: new THREE.Vector3(0, 0, 1) }, // placeholder; host MUST drive via update({sunDir})
				uPDet: { value: PDET },
				uBk: { value: BREAKUP ? 1 : 0 },
				uTime: { value: 0 },
				uWave: { value: WAVE },
				uExposure: { value: Number(this.opts.exposure ?? 1.0) },
				uSaturation: { value: Number(this.opts.saturation ?? 1.0) },
				uDbgChart: { value: DBG_CHART ? 1 : 0 },
				uWorldRotation: { value: new THREE.Matrix3() },
				// atlas: LAVA only (declared under `#if USE_LAVA` in FRAG_D)
				...(USE_LAVA ? { uAtlas: { value: atlasTex } } : {}),
			},
			glslVersion: BAKED ? THREE.GLSL3 : null,
		});
		const world = this._world || new THREE.Group();
		const planetMesh = new THREE.Mesh(geo, mat);
		planetMesh.frustumCulled = false;
		planetMesh.visible = false; // shown when the bakes complete
		world.add(planetMesh);
		this._world = world;
		// During a rebuild the OLD mesh/material stay live (visible + driven by
		// update()/setWorldRotation) until the new bakes land; the swap happens in
		// _onBakesDone. On a first build there is no old mesh, so adopt now.
		if (!isRebuild) { this._planetMesh = planetMesh; this._mat = mat; }

		// ── atmosphere halo (optional; PHASE 2) ──────────────────────────────────
		// A single cheap BackSide additive Fresnel shell, rim-lit by uSunDir. Ported
		// from legacy planet-3rdgen (per-type colour). Built ONCE and kept across
		// rebuilds (it depends only on planet TYPE, not terrain). No extra passes,
		// so it does not regress XR perf.
		if (ATMO && !this._atmosphere) {
			const pt = PLANET_TYPES[this.spec.type] || PLANET_TYPES.EARTHLIKE;
			// gas giants tint the halo from their seed-derived palette (gasPalette
			// .atmo) so it matches the painted bands; other types use the type atmo.
			const rgb = String((USE_GAS && GAS ? GAS.atmo : pt.atmo) || "0.5, 0.7, 1.0").split(",").map(Number);
			const atmoMat = new THREE.ShaderMaterial({
				vertexShader: /* glsl */`
					varying vec3 vNormalV;
					varying vec3 vNormalW;
					void main() {
						vNormalV = normalize(normalMatrix * normal);
						vNormalW = normalize(mat3(modelMatrix) * normal);
						gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 0.9); // legacy w=0.9 halo enlarge
					}
				`,
				fragmentShader: /* glsl */`
					precision highp float;
					uniform vec3 uColor;
					uniform vec3 uSunDir;
					varying vec3 vNormalV;
					varying vec3 vNormalW;
					void main() {
						// rim (silhouette) glow: BackSide shell, brightest where the
						// surface grazes the view direction (view +Z = toward camera).
						float fres = pow(max(0.0, 0.5 - dot(vNormalV, vec3(0.0, 0.0, 1.0))), 1.9); // legacy Fresnel
						// warm the lit limb, keep the night limb faint.
						float sun = clamp(dot(vNormalW, normalize(uSunDir)) * 0.5 + 0.5, 0.0, 1.0);
						gl_FragColor = vec4(uColor * fres, 1.0); // legacy: color * intensity, no sun term
					}
				`,
				uniforms: {
					uColor: { value: new THREE.Vector3(rgb[0] || 0.5, rgb[1] || 0.7, rgb[2] || 1.0) },
					uSunDir: { value: new THREE.Vector3(0, 0, 1) }, // placeholder; host MUST drive via update({sunDir})
				},
				blending: THREE.AdditiveBlending,
				side: THREE.BackSide,
				transparent: true,
				depthWrite: false,
			});
			const atmosphere = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 16), atmoMat);
			atmosphere.scale.setScalar(1.0125); // legacy halo scale (with w=0.9 above)
			atmosphere.frustumCulled = false;
			atmosphere.renderOrder = 1; // drawn after the opaque planet
			world.add(atmosphere);
			this._atmosphere = atmosphere;
			this._atmoMat = atmoMat;
		}

		// ── chunked bake runner: one chart-sized GPU job per frame ───────────────
		// One monolithic bake stalled the Pico compositor for seconds — in-headset
		// that reads as a gut punch. Chunks keep every frame under budget; total
		// load takes slightly longer, which is the right trade (mre 2026-07-15).
		// build() ENQUEUES the tasks; update() drains up to chunksPerFrame of them
		// per frame (the bench's processBake, pumped from its animation loop). The
		// bench's `reveal` and `zoomApply` task kinds were HOST/canvas concerns
		// (they render into the default framebuffer via the host scene/camera) and
		// do NOT survive extraction — the lib reveals by flipping planetMesh.visible
		// when the queue empties; the host pays first-draw costs itself.
		const runBakeTask = t => {
			// THE ROOT CAUSE OF THE ENTIRE ARTIFACT SAGA (bench v27..v39): while an
			// XR session is presenting, three.js substitutes the XR camera and
			// PER-EYE VIEWPORTS into EVERY renderer.render() — even renders into
			// offscreen bake targets. In-session bakes therefore wrote
			// eye-viewport-shaped fragments instead of the intended regions (proven
			// 2026-07-17). Bakes MUST run with xr.enabled off; save/restore around
			// each one so the host's presenting session is untouched.
			const wasXREnabled = renderer.xr.enabled;
			// ...and RESTORE the host's render target, never null: in XR three binds the
			// eye framebuffer ONCE per frame (WebXRManager onAnimationFrame) and the host's
			// scene render follows THIS bake in the same frame. setRenderTarget(null) sent
			// that render to the invisible canvas → the headset showed BLACK for as long
			// as bake chunks ran (~20 s per planet on the Pico, 2026-10-09).
			const hostRT = renderer.getRenderTarget();
			renderer.xr.enabled = false;
			const isA = t.kind === "A";
			const dstRT = isA ? rtA : t.rt;
			const uniforms = isA ? bakeUniforms : matBakeUniforms;
			if (!isA) {
				matBakeUniforms.uOutMode.value = t.mode;
				matBakeUniforms.uZoomIdx.value = t.layer;
				matBakeUniforms.uTexels.value = ARES;
			}
			bakeQuad.material = isA ? matA : matMat;
			// bakeSlice: setRenderTarget applies the target's scissor (cleared again below)
			if (t.scissor) { dstRT.scissor.set(...t.scissor); dstRT.scissorTest = true; }
			if (t.tile) {
				const [x, y, tw, th] = t.tile;
				const S = dstRT.width;
				uniforms.uUvXform.value.set(tw / S, th / S, x / S, y / S);
				const wasClear = renderer.autoClear;
				renderer.autoClear = false;
				dstRT.viewport.set(x, y, tw, th);
				if (t.layer !== undefined) renderer.setRenderTarget(dstRT, t.layer);
				else renderer.setRenderTarget(dstRT);
				renderer.render(bakeScene, bakeCam);
				dstRT.viewport.set(0, 0, S, dstRT.height);
				renderer.autoClear = wasClear;
			} else {
				uniforms.uUvXform.value.set(1, 1, 0, 0);
				if (t.layer !== undefined) renderer.setRenderTarget(dstRT, t.layer);
				else renderer.setRenderTarget(dstRT);
				renderer.render(bakeScene, bakeCam);
			}
			if (t.scissor) {
				dstRT.scissorTest = false;
				dstRT.scissor.set(0, 0, dstRT.width, dstRT.height);
				// end the submission here so this slice is its own GPU job
				if (t.flush) renderer.getContext().flush();
			}
			renderer.setRenderTarget(hostRT);
			renderer.xr.enabled = wasXREnabled;
		};
		// FULL-TARGET renders ONLY. Every chunked variant the bench tried (scissor
		// v27, eager-alloc v29, quarter charts v30, scratch+copyTexSubImage2D v34)
		// intermittently corrupted the big textures on the Pico — classic Adreno
		// UBWC partial-update corruption, confirmed by per-chunk pixel readback
		// (some chunks exact, neighbors garbage). The full-target bakes were the
		// only ones verified CLEAN in-headset. One render per frame; the hitches
		// are load-time, not mid-XR. (BTILE>0 tiles a layer only when the host
		// explicitly opts in — kept for experiments, off by default.)
		// BTILE > 0 also tiles the atlas bake (kind A): on weak desktop GPUs one full-atlas draw of
		// the erosion shader exceeded the driver's preemption timeout and reset the context
		// (Adelheid, Intel HD Graphics, 2026-10-11). runBakeTask already handles A with a tile
		// (the warm-up draws use it); the host opts in with `btile` (game: weak GPUs → 128).
		const bakeTasks = [];
		if (SLICE) {
			// bakeSlice: the SAME draws the unsliced queue below issues (same viewport, same uv
			// transform → bit-identical texels), each cut by a scissor rect into tileSize² slices:
			// the GPU shades only the slice, so every draw is short. Order as below.
			const TS = SLICE.tileSize, flush = SLICE.flush;
			const sliced = (task, x0, y0, w, h) => {
				for (let y = y0; y < y0 + h; y += TS) for (let x = x0; x < x0 + w; x += TS)
					bakeTasks.push({ ...task, scissor: [x, y, Math.min(TS, x0 + w - x), Math.min(TS, y0 + h - y)], flush });
			};
			const SA = BRES * ATLASN;
			if (BTILE > 0) {
				for (let y = 0; y < SA; y += BTILE) for (let x = 0; x < SA; x += BTILE) {
					const tile = [x, y, Math.min(BTILE, SA - x), Math.min(BTILE, SA - y)];
					sliced({ kind: "A", tile }, ...tile);
				}
			} else {
				sliced({ kind: "A" }, 0, 0, SA, SA);
			}
			if (BAKED) for (let k = 0; k < NCC; k++) for (let mode = 0; mode < 2; mode++) {
				const rt = mode ? rtMatN : rtMatC;
				if (BTILE > 0) {
					const n = ARES / BTILE;
					for (let ty = 0; ty < n; ty++) for (let tx = 0; tx < n; tx++) {
						const tile = [tx * BTILE, ty * BTILE, BTILE, BTILE];
						sliced({ kind: "M", mode, rt, layer: k, tile }, ...tile);
					}
				} else {
					sliced({ kind: "M", mode, rt, layer: k }, 0, 0, ARES, ARES);
				}
			}
		} else if (BTILE > 0) {
			const SA = BRES * ATLASN; // rtA is SA × SA (not ARES)
			for (let y = 0; y < SA; y += BTILE) for (let x = 0; x < SA; x += BTILE) {
				bakeTasks.push({ kind: "A", tile: [x, y, Math.min(BTILE, SA - x), Math.min(BTILE, SA - y)] });
			}
		} else {
			bakeTasks.push({ kind: "A" });
		}
		if (BAKED && !SLICE) for (let k = 0; k < NCC; k++) for (let mode = 0; mode < 2; mode++) {
			const rt = mode ? rtMatN : rtMatC;
			if (BTILE > 0) {
				const n = ARES / BTILE;
				for (let ty = 0; ty < n; ty++) for (let tx = 0; tx < n; tx++)
					bakeTasks.push({ kind: "M", mode, rt, layer: k, tile: [tx * BTILE, ty * BTILE, BTILE, BTILE] });
			} else {
				bakeTasks.push({ kind: "M", mode, rt, layer: k });
			}
		}

		// warm the shader programs first, THEN enqueue the bake queue (do NOT drain
		// it here — update() pumps it over frames). The bench's ~2s Pico choke at
		// chunk 25/72 was NOT bake work: it was the driver compiling FRAG_MAT on its
		// first draw (and FRAG_D on reveal). Warm all programs up front via
		// compileAsync (KHR_parallel_shader_compile — off-thread, frames keep
		// pumping), only then start the task queue. A dummy camera keeps this
		// self-contained (the lib owns no camera). No render ever targets the
		// default framebuffer — the host owns the canvas.
		if (typeof window !== 'undefined' && window.__tierLog) window.__tierLog.push({ t: Math.round(performance.now()), event: 'phase:gl-setup-done', planet: this.spec.seed });
		const warmCam = new THREE.PerspectiveCamera();
		const warmHostRT = renderer.getRenderTarget();
		try {
			// three keys programs by output target (colorspace define): compiling
			// against the canvas does NOT warm the render-target variant the bake
			// actually uses. Compile with the real target bound.
			// NEVER keep a target bound across an await: frames (and other planets'
			// builds) run meanwhile — the host's frames rendered into rtA, and a
			// concurrent build captured rtA as "the host target" and restored it for
			// good (desktop went black, 2026-10-09). compileAsync compiles
			// synchronously in the call (reading the bound target), then only polls.
			bakeQuad.material = matA;
			renderer.setRenderTarget(rtA);
			const compileA = renderer.compileAsync(bakeScene, bakeCam);
			renderer.setRenderTarget(warmHostRT);
			await compileA;
			if (matMat) {
				bakeQuad.material = matMat;
				const hostNow = renderer.getRenderTarget();
				renderer.setRenderTarget(rtMatC);
				const compileM = renderer.compileAsync(bakeScene, bakeCam);
				renderer.setRenderTarget(hostNow);
				await compileM;
			}
			// real-resolution sphere: Adreno specializes per draw, and an 8-segment
			// warm sphere did NOT absorb the ~1.08s reveal stall (bench v44). Warm
			// the EXACT geometry the reveal draws.
			const warmScene = new THREE.Scene();
			warmScene.add(new THREE.Mesh(new THREE.SphereGeometry(1, SRES, SRES / 2), mat));
			await renderer.compileAsync(warmScene, warmCam);
			warmScene.children[0].geometry.dispose();
			// pre-upload the big CPU data textures (mask/biome are ~21MB of floats)
			// — otherwise the FIRST bake draw pays the upload as a one-time stall.
			for (const tx of [maskTex, biomeTex, mtnTex, segTex]) renderer.initTexture(tx);
			// Adreno specializes programs on the FIRST DRAW, not at link — tiny REAL
			// draws of the EXACT bake variants (layered array-texture target for the
			// material bake) pay that here, offscreen, instead of at chunk time.
			runBakeTask({ kind: "A", tile: [0, 0, 4, 4] });
			if (matMat) {
				runBakeTask({ kind: "M", mode: 0, rt: rtMatC, layer: 0, tile: [0, 0, 4, 4] });
				runBakeTask({ kind: "M", mode: 1, rt: rtMatN, layer: 0, tile: [0, 0, 4, 4] });
			}
			// NOTE: the bench also fired a tiny REAL reveal draw of the planet mesh
			// (renderer.render(warmScene, camera) into a 4x4 canvas viewport) to pay
			// the render-target→sampler layout transition of the freshly-baked
			// ~200MB atlases. That draw targets the DEFAULT framebuffer via the host
			// camera — a host concern the lib cannot own — so it does NOT survive
			// extraction. The host pays that first-draw cost itself (see host-next).
		} catch (e) {
			console.warn("imperial-planet/next: shader warmup failed (continuing)", e);
		}

		if (typeof window !== 'undefined' && window.__tierLog) window.__tierLog.push({ t: Math.round(performance.now()), event: 'phase:warm-done', planet: this.spec.seed });
		// hand the queue to update(): it drains up to chunksPerFrame tasks per frame
		// and reveals the mesh (and resolves `ready`) when the last one lands. We do
		// NOT drain synchronously — that flooded the Pico compositor (the regression
		// this restores). object3d already exists (mesh added above, invisible), so
		// the host can add it to the scene the instant build() resolves.
		this._runBakeTask = runBakeTask;
		this._bakeTasks = bakeTasks;
		this._building = true;
		this._onBakesDone = () => {
			planetMesh.visible = true;
			if (typeof window !== 'undefined' && window.__tierLog) window.__tierLog.push({ t: Math.round(performance.now()), event: 'phase:bakes-done', planet: this.spec.seed });
			if (this._grid) this._grid.visible = this._gridWanted !== false;
			// Culling stays OFF during warmup/bakes (the warm draws must not be
			// culled away) and goes ON at reveal: a HOST with a whole system of
			// these (the game: 19 planets in XR) otherwise renders every planet
			// every frame, in both eyes — measured 2.5 M triangles/frame vs 78 k
			// with culling on the Pico (2026-09-28). Unit-sphere geometry → the
			// bounding sphere is exact.
			world.traverse(o => { if (o.isMesh || o.isLine || o.isLineSegments) o.frustumCulled = true; });
			if (isRebuild) {
				// swap the freshly-baked mesh in for the old one, then retire the old
				// mesh + its GL resources. Carry live per-frame uniform state
				// (sun/time/world-rotation) over so there is no 1-frame flash.
				if (prevMat) {
					mat.uniforms.uSunDir.value.copy(prevMat.uniforms.uSunDir.value);
					mat.uniforms.uTime.value = prevMat.uniforms.uTime.value;
					mat.uniforms.uWorldRotation.value.copy(prevMat.uniforms.uWorldRotation.value);
				}
				this._planetMesh = planetMesh;
				this._mat = mat;
				if (prevMesh) { world.remove(prevMesh); disposeObject(prevMesh); }
				for (const d of prevDisposables) d.dispose?.();
				if (prevGrid) { world.remove(prevGrid); disposeObject(prevGrid); this._grid = null; }
				if (prevGridVisible) this.setGridVisible(true); // rebuild from new heights
			}
			// ── release the bake-only pipeline NOW, not at dispose() ──────────────
			// With the queue drained nothing renders matA/matMat again: live shading
			// samples only rtA (+ rtMatC/rtMatN when BAKED). Keeping the bake
			// materials + the worker's mask/biome/mtn/seg DataTextures alive for the
			// planet's whole life cost ~10 MB GPU + ~10 MB JS heap PER PLANET, and
			// their uniform refs were the retainer surface of the measured ~9 MB/
			// cycle heap leak. maskData (the CPU array) stays alive via baseAt — the
			// grid + tile-center services need it; only the GPU texture + the
			// texture→array refs are dropped here.
			matA.dispose();
			if (matMat) matMat.dispose();
			bakeQuad.geometry.dispose();
			bakeQuad.material = null;
			for (const tx of [maskTex, mtnTex, segTex]) { tx.dispose(); tx.image.data = null; }
			bakeUniforms.uMask.value = null;
			bakeUniforms.uMtns.value = null;
			bakeUniforms.uSegs.value = null;
			if (BAKED) {
				// the baked FRAG_D variant never samples uBiome (compiled out by
				// #if) — the 6.5 MB biome array + its texture are pure bake inputs.
				// !BAKED (quality high) keeps them: the live shader samples uBiome.
				biomeTex.dispose();
				biomeTex.image.data = null;
				matBakeUniforms.uBiome.value = null;
				mat.uniforms.uBiome.value = null;
			}
			this._runBakeTask = null; // its closure holds the whole bake graph
			this._onBakesDone = null;
			this._building = false;
			this.opts.onReady?.(this);
			this._readyResolve(this);
		};

		// ── grid overlay (lazy) + tile-service closures ──────────────────────────
		// draped base height for a unit direction — the CPU mirror of the bake's
		// base+bump field (mountains/ridges), clamped to sea. Shared by the grid
		// and the tile-center services below.
		const ss = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
		const chartAt = d => {
			let best = 0, bd = -2;
			charts.forEach((c, k) => { const dd = d.dot(c.dir); if (dd > bd) { bd = dd; best = k; } });
			const [x, y] = chartCoords(d, charts[best]);
			return { k: best, x, y };
		};
		const baseAt = d => {
			const { k, x, y } = chartAt(d);
			const c = charts[k];
			const mx = Math.min(MRES - 1, Math.max(0, Math.round((x / c.span + 0.5) * MRES - 0.5)));
			const my = Math.min(MRES - 1, Math.max(0, Math.round((y / c.span + 0.5) * MRES - 0.5)));
			const mo = 2 * (((Math.floor(k / ATLASN) * MRES + my) * MRES * ATLASN) + (k % ATLASN) * MRES + mx);
			const mRaw = maskData[mo];
			const floor = FLOOR_SHELF + (OCEANH - FLOOR_SHELF) * maskData[mo + 1];
			let h = floor + (WATER - floor) * ss(0.10, 0.51, mRaw)
				+ (PLAIN - WATER) * ss(0.49, 0.62, mRaw);
			const flat = h;
			let S = 0;
			for (let j = mtnStart[k]; j < mtnStart[k] + mtnCount[k]; j++) {
				const dx = x - mtns[j * 4], dy = y - mtns[j * 4 + 1];
				const dist = Math.hypot(dx, dy), R = APO * BUMPR;
				if (dist < R) {
					const t = 1 - dist / R;
					S += Math.exp(SMK * mtns[j * 4 + 2] * t * t * (3 - 2 * t)) - 1;
				}
			}
			for (let j = segStart[k]; j < segStart[k] + segCount[k]; j++) {
				const o = j * 8;
				const abx = segs[o + 2] - segs[o], aby = segs[o + 3] - segs[o + 1];
				const L2 = Math.max(abx * abx + aby * aby, 1e-9);
				let tt = ((x - segs[o]) * abx + (y - segs[o + 1]) * aby) / L2;
				tt = Math.min(1, Math.max(0, tt));
				const dist = Math.hypot(x - segs[o] - abx * tt, y - segs[o + 1] - aby * tt);
				const R = APO * BUMPR;
				if (dist < R) {
					const s = 1 - dist / R;
					const phT = segs[o + 4] + (segs[o + 5] - segs[o + 4]) * tt;
					S += Math.exp(SMK * phT * s * s * (3 - 2 * s)) - 1;
				}
			}
			h = flat + Math.log(1 + S) / SMK;
			return Math.max(h, WATER);
		};
		this._baseAt = baseAt;

		this._buildGridFn = () => {
			const pts = [];
			const v = new THREE.Vector3();
			for (let i = 0; i < N; i++) {
				const b = sphere.boundaries[i];
				for (let j = 0; j < b.length; j++) {
					const a = b[j], c2 = b[(j + 1) % b.length];
					for (const pt of [a, c2]) {
						v.set(pt[0], pt[1], pt[2]).normalize();
						const r = 1 + (baseAt(v) - WATER) / K + 0.0015;
						pts.push(v.x * r, v.y * r, v.z * r);
					}
				}
			}
			const g = new THREE.BufferGeometry();
			g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(pts), 3));
			return new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: opts.gridColor ?? 0xe0b84d, transparent: true, opacity: opts.gridOpacity ?? 0.5 }));
		};

		this._built = true;
		return this;
	}

	// ── contract surface ─────────────────────────────────────────────────────
	get object3d() { return this._world; }
	get planetMesh() { return this._planetMesh; }
	get atmosphere() { return this._atmosphere; }
	get uniforms() { return this._mat ? this._mat.uniforms : null; }
	get tileCount() { return this._N; }

	/** true while the per-frame bake queue is still draining (mesh hidden). */
	get building() { return this._building; }

	/**
	 * Promise that resolves to `this` when the bakes finish streaming in and the
	 * mesh is revealed. build() resolves as soon as setup+warmup+enqueue are done
	 * (object3d exists, addable immediately); `ready` resolves frames later, once
	 * update() has pumped the last bake task. (opts.onReady fires at the same
	 * moment for a callback-style host.)
	 */
	get ready() { return this._ready; }

	/**
	 * Per-frame host hook. sunDir/time drive the live uniforms; cameraPosition is
	 * ignored (three supplies the built-in `cameraPosition` uniform from the
	 * host's camera at render time).
	 */
	update({ sunDir, time } = {}) {
		if (!this._mat) return;
		const u = this._mat.uniforms;
		if (sunDir) {
			u.uSunDir.value.set(sunDir.x, sunDir.y, sunDir.z).normalize();
			if (this._atmoMat) this._atmoMat.uniforms.uSunDir.value.copy(u.uSunDir.value);
		}
		if (time != null) u.uTime.value = time;
		this._pumpBakes();
	}

	// The bench's processBake(), pumped from the host's animation loop via
	// update(): drain up to chunksPerFrame chart-sized bake jobs per frame, then
	// reveal + resolve `ready` when the queue empties. One render per frame under
	// the XR preset (chunksPerFrame=1) so the Pico compositor never gut-punches.
	_pumpBakes() {
		const tasks = this._bakeTasks;
		if (!tasks || !tasks.length) return;
		// GLOBAL per-frame bake budget: without it, N planets building at once
		// each drained their own chunksPerFrame — 6 concurrent low-tier builds
		// submitted 6× the intended per-frame GPU work, which on Adreno (Moto
		// G84, Firefox GPU process) faulted the driver (IOCTL_KGSL_GPU_COMMAND
		// errno 35 EDEADLK → GPU process SIGKILL → black page). The shared
		// spend-counter resets when the host renderer advances a frame; each
		// planet may spend up to ITS chunksPerFrame from the shared pool.
		const f = this.opts.renderer?.info?.render?.frame ?? -1;
		if (f !== _bakeGate.frame) { _bakeGate.frame = f; _bakeGate.spent = 0; }
		while (_bakeGate.spent < this._chunksPerFrame && tasks.length) {
			_bakeGate.spent++;
			this._runBakeTask(tasks.shift());
		}
		if (!tasks.length) {
			this._bakeTasks = null;
			this._onBakesDone?.();
		}
	}

	/**
	 * The clean setter that replaces the bench's XR syncXRWorldUniforms bridge.
	 * The host nav drives the planet's world orientation; the shader lights in
	 * world space, so it needs the matching rotation. Pass object3d.quaternion
	 * (or any THREE.Quaternion) after you rotate the planet.
	 */
	setWorldRotation(quat) {
		if (!this._mat) return;
		const m4 = new THREE.Matrix4().makeRotationFromQuaternion(quat);
		this._mat.uniforms.uWorldRotation.value.setFromMatrix4(m4);
	}

	setGridVisible(v) {
		this._gridWanted = !!v;
		if (v && !this._grid && this._buildGridFn) {
			this._grid = this._buildGridFn();
			this._world.add(this._grid);
		}
		// never show the grid over a planet that is still baking (mesh hidden): it
		// floated alone in space for the whole build ("hexgrid while loading");
		// _onBakesDone shows it with the mesh.
		if (this._grid) this._grid.visible = !!v && !!this._planetMesh?.visible;
	}

	/** Set the hex-grid line opacity live (0..1). Builds the grid if needed. */
	setGridOpacity(o) {
		if (!this._grid && this._buildGridFn) {
			this._grid = this._buildGridFn();
			this._world.add(this._grid);
		}
		if (this._grid) {
			this._grid.material.opacity = o;
			this._grid.material.transparent = o < 1;
			this._grid.visible = this._gridWanted !== false && !!this._planetMesh?.visible;
		}
	}

	// ── tile services (data only — the host owns the Raycaster) ────────────────

	// LEGACY-ONLY. next's mesh is a smooth SphereGeometry with no per-triangle→tile
	// face map, so a raycast faceIndex cannot resolve a hex tile. Use
	// tileAtPoint(hit.point) / tileAtDirection(dir) instead (nearest tile-center
	// direction). Kept for contract compatibility; always returns -1 here.
	tileFromFace(_faceIndex) { return -1; }

	/**
	 * Tile whose center DIRECTION is nearest `dir` (max dot over the worker's
	 * per-tile dirs). `dir` is in the planet's LOCAL/unrotated frame (child-of-
	 * object3d space); it is not mutated. Returns -1 before build().
	 */
	tileAtDirection(dir) {
		const dirs = this._dirs;
		if (!dirs) return -1;
		_pickDir.copy(dir);
		const len = _pickDir.length();
		if (len < 1e-12) return -1;
		_pickDir.divideScalar(len);
		let best = -1, bd = -Infinity;
		for (let i = 0; i < dirs.length; i++) {
			const dd = dirs[i].dot(_pickDir);
			if (dd > bd) { bd = dd; best = i; }
		}
		return best;
	}

	/**
	 * Tile under a WORLD-space point (e.g. a raycast hit on object3d). Inverse-
	 * transforms the point through object3d's world matrix (undoing the host's
	 * position/rotation/scale, incl. XR locomotion) into the planet's local frame,
	 * then delegates to tileAtDirection. NOTE: setWorldRotation only mirrors the
	 * world rotation into the shader for LIGHTING — it does not move geometry, so
	 * it must NOT be applied here; object3d's matrix already IS the geometry
	 * transform. `point` is not mutated. Returns -1 before build().
	 */
	tileAtPoint(point) {
		if (!this._dirs || !this._world) return -1;
		this._world.updateWorldMatrix(true, false);
		_pickLocal.copy(point);
		this._world.worldToLocal(_pickLocal);
		return this.tileAtDirection(_pickLocal);
	}

	tileNeighbors(i) { return this._neighbors ? this._neighbors[i] : []; }

	/** mesh-local (child-of-object3d) draped center of tile i. */
	getTileCenter(i, out = new THREE.Vector3()) {
		const d = this._dirs[i];
		const r = 1 + (this._baseAt(d) - this._WATER) / this._K;
		return out.set(d.x * r, d.y * r, d.z * r);
	}

	/** unit surface normal of tile i (radial). */
	getTileNormal(i, out = new THREE.Vector3()) {
		const d = this._dirs[i];
		return out.set(d.x, d.y, d.z).normalize();
	}

	getTileTransform(i) {
		const position = this.getTileCenter(i);
		const normal = this.getTileNormal(i);
		const quaternion = new THREE.Quaternion().setFromUnitVectors(_UP, normal);
		return { position, normal, quaternion };
	}

	placeOnTile(i, obj, { offset = 0, orient = true } = {}) {
		const { position, normal, quaternion } = this.getTileTransform(i);
		if (offset) position.addScaledVector(normal, offset);
		obj.position.copy(position);
		if (orient) obj.quaternion.copy(quaternion);
		this._world.add(obj);
		return obj;
	}

	// per-tile hex boundary loops (nested [tile][vertex][x,y,z]), DRAPED to the
	// unit-sphere surface (radius ~1 + terrain height) so they sit on the planet
	// exactly like getTileCenter + the grid. NOTE: the raw hexasphere loops are at
	// the sphere's native radius (~4 for size 18) — returning those raw floated
	// the game's hover/border kilometres off the surface and 4× oversized. Cached;
	// invalidated on the terraform re-bake (this._drapedBoundaries = null there).
	get boundaries() {
		if (this._drapedBoundaries) return this._drapedBoundaries;
		if (!this._boundaries || !this._baseAt) return this._boundaries;
		const v = new THREE.Vector3();
		this._drapedBoundaries = this._boundaries.map(loop => loop.map(pt => {
			v.set(pt[0], pt[1], pt[2]).normalize();
			const r = 1 + (this._baseAt(v) - this._WATER) / this._K + 0.0015;
			return [v.x * r, v.y * r, v.z * r];
		}));
		return this._drapedBoundaries;
	}

	/**
	 * Terraform re-bake. Merges `patches` ({ [tileIndex]: { t?: cellId, h?:
	 * gameplayHeight(-1..1) } }) into this.spec.patches and re-runs generation +
	 * the GPU bakes with the new terrain. Reuses the SAME chunked bake runner as
	 * build(): _generate() ENQUEUES the tasks into this._bakeTasks and update()'s
	 * _pumpBakes streams them frame-by-frame (NO synchronous drain). The old mesh
	 * stays visible during the re-bake and is swapped out on completion; object3d
	 * is preserved (same Group), so the host does NOT re-add it. Resolves to `this`
	 * once the bakes have finished streaming (this.ready resolves at that moment).
	 */
	async applyPatches(patches) {
		this.spec = { ...this.spec, patches: { ...(this.spec.patches || {}), ...patches } };
		if (!this._built) return this.build(); // not built yet → normal first build
		// fresh ready promise for THIS re-bake cycle (host may await planet.ready)
		this._ready = new Promise(res => { this._readyResolve = res; });
		await this._generate(true); // spins a fresh worker w/ the merged patches,
		                            // rebuilds the pipeline, ENQUEUES the bakes
		await this._ready;          // resolves when update() pumps the last bake in
		return this;
	}

	dispose() {
		if (this._world) disposeObject(this._world);
		for (const d of this._disposables) d.dispose?.();
		this._disposables.length = 0;
		// NOTE: the init worker is a SHARED pool worker (see getPoolWorker) reused
		// by every planet — dispose() must NOT terminate it. It persists idle for
		// the session. Only this planet's own GL resources (RTs/DataTextures above,
		// mesh/grid below) are freed here.
		this._world = null;
		this._planetMesh = null;
		this._mat = null;
		this._atmosphere = null;
		this._atmoMat = null;
		this._grid = null;
		// LEAK FIX: the bake-runner closures capture the mask/biome DataTextures
		// (each backed by a multi-MB Float32Array from the worker). _disposables
		// frees the GPU side, but these closure refs kept the DataTexture objects
		// — and their .image.data arrays — alive after dispose (measured: ~13 MB
		// of JS heap retained per build/dispose cycle). Null them so they GC.
		this._runBakeTask = null;
		this._bakeTasks = null;
		this._onBakesDone = null;
		// tile-service data pulled from the worker (per-tile dirs/adjacency/
		// boundaries) — only needed while the planet is live.
		this._dirs = null;
		this._neighbors = null;
		this._boundaries = null;
		this._drapedBoundaries = null;
		this._baseAt = null;
		// LEAK FIX: _buildGridFn captures baseAt (→ maskData, ~3 MB) AND the
		// worker's whole `sphere` (nested boundaries/neighbors) — nulling _baseAt
		// alone left all of it reachable through this closure.
		this._buildGridFn = null;
		this._built = false;
	}
}

export default Planet;
